<template>
  <div class="zoom-container bg-black text-white no-scroll overflow-hidden">
    <div class="toast-container">
      <div v-for="t in toasts" :key="t.id" class="toast" :class="'toast-' + t.type">
        {{ t.message }}
      </div>
    </div>

    <div v-if="isJoinDialogOpen || isConnecting" class="modal-backdrop">
      <div class="modal-card join-modal">
        <div class="modal-section text-center">
          <span class="material-icons text-primary" style="font-size: 56px; margin-bottom: 16px">groups</span>
          <div class="text-h5 font-bold">Classroom Entrance</div>
          <div class="text-caption text-grey-5 mt-sm">Enter your display name to join the LiveKit session.</div>
        </div>

        <div class="modal-section px-xl">
          <label class="input-label">Your Display Name</label>
          <input type="text" v-model="userName" class="custom-input mb-lg" autofocus />

          <div class="row pt-sm">
            <div class="col-6 pr-sm">
              <button class="btn btn-primary full-width py-md pill-btn" @click="handleJoin()" :disabled="!userName.trim()">
                <div class="flex items-center justify-center gap-sm">
                  <span class="material-icons" style="font-size: 20px">meeting_room</span>
                  <span class="font-medium">Join</span>
                </div>
              </button>
            </div>
            <div class="col-6 pl-sm">
              <button class="btn btn-outline full-width py-md pill-btn" @click="handleCancel()">
                <div class="flex items-center justify-center gap-sm">
                  <span class="material-icons" style="font-size: 20px">close</span>
                  <span class="font-medium">Cancel</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div v-if="isConnecting" class="inner-loading-overlay">
          <div class="spinner"></div>
          <div class="text-white mt-sm font-medium">Connecting to meeting...</div>
        </div>
      </div>
    </div>

    <div v-if="isRoomsDashboardOpen" class="modal-backdrop">
      <div class="modal-card breakout-modal text-white">
        <div class="modal-header relative flex items-center">
          <div class="flex items-center gap-sm">
            <div class="font-bold text-sm">Breakout rooms</div>
            <button v-if="isTeacher && !isRoomsLoading" class="btn btn-flat btn-round btn-sm text-blue" title="Add Room" @click="createBreakoutRoom()">
              <span class="material-icons" style="font-size: 20px">add_circle</span>
            </button>
            <div v-if="isRoomsLoading" class="spinner-xs ml-sm"></div>
          </div>
          <span
            class="material-icons cursor-pointer text-grey-5 hover-white absolute"
            style="font-size: 20px; right: 20px; top: 18px; cursor: pointer"
            @click="isRoomsDashboardOpen = false"
            >close</span
          >
        </div>

        <div class="modal-body scroll p-none">
          <div v-for="(members, roomName) in displayRooms" :key="roomName" class="accordion-item" v-if="roomName !== 'MainRoom' || isTeacher">
            <div class="accordion-header flex space-between" @click="$set(expandedRooms, roomName, !expandedRooms[roomName])">
              <div class="flex items-center flex-grow-1">
                <span class="material-icons expand-icon">{{ expandedRooms[roomName] ? 'expand_more' : 'chevron_right' }}</span>
                <span class="room-title">{{ getRoomTitle(roomName) }}</span>

                <div v-if="isTeacher && roomName !== 'MainRoom'" class="flex items-center gap-xs ml-md opacity-0 hover-opacity-100 transition-opacity">
                  <span
                    class="material-icons text-grey-5 hover-white cursor-pointer"
                    style="font-size: 16px"
                    title="Rename"
                    @click.stop="promptRenameRoom(roomName)"
                    >edit</span
                  >
                  <span
                    class="material-icons text-red hover-red-light cursor-pointer"
                    style="font-size: 16px"
                    title="Delete"
                    @click.stop="confirmDeleteRoom(roomName)"
                    >delete</span
                  >
                </div>
              </div>
              <div class="flex items-center gap-md">
                <div class="flex items-center text-blue">
                  <span class="material-icons text-sm mr-xs">person_outline</span>
                  <span class="text-sm">{{ members ? members.length : 0 }}</span>
                </div>
                <div class="join-action">
                  <span v-if="roomId === roomName" class="text-grey-5 font-medium text-sm">Joined</span>
                  <button v-else class="btn btn-blue btn-sm" @click.stop="joinBreakoutRoom(roomName)">Join</button>
                </div>
              </div>
            </div>

            <div v-if="expandedRooms[roomName] && members && members.length > 0" class="accordion-content">
              <ul class="member-list">
                <li v-for="m in members" :key="m.userName" class="member-item flex space-between">
                  <span class="text-grey-4 text-sm">{{ m.userName }} {{ m.userName === userName ? '(Me)' : '' }}</span>

                  <div v-if="isTeacher && m.userName !== userName" class="relative">
                    <button class="btn btn-flat btn-text-blue" @click.stop="toggleMoveMenu($event, m.userName)">Assign</button>
                    <div v-if="moveMenuState[m.userName]" class="dropdown-menu shadow-2 no-transition" :style="moveMenuStyles[m.userName]">
                      <ul>
                        <li v-show="roomName !== 'MainRoom'" @click="handleAssign(m.userName, 'MainRoom')">
                          <div class="flex items-center gap-xs">
                            <span class="material-icons" style="font-size: 16px">home</span>
                            <span>Main Session</span>
                          </div>
                        </li>
                        <li
                          v-for="targetRoom in Object.keys(displayRooms)"
                          v-show="targetRoom !== roomName && targetRoom !== 'MainRoom'"
                          :key="targetRoom"
                          @click="handleAssign(m.userName, targetRoom)">
                          {{ getRoomTitle(targetRoom) }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="modal-footer flex flex-end gap-sm py-md px-lg">
          <button class="btn btn-outline btn-sm pill-btn" @click="collapseAll" :disabled="isAllCollapsed">Collapse all</button>
          <button class="btn btn-outline btn-sm pill-btn" @click="expandAll" :disabled="isAllExpanded">Expand all</button>
        </div>

        <div v-if="isConnecting" class="inner-loading-overlay">
          <div class="spinner"></div>
          <div class="text-white mt-sm font-medium">Joining Room....</div>
        </div>

        <div v-if="isRoomsLoading && !isConnecting" class="inner-loading-overlay breakout-updating-overlay">
          <div class="breakout-spinner"></div>
          <div class="text-white mt-sm font-medium" style="font-size: 14px">{{ roomsLoadingLabel || 'Updating...' }}</div>
        </div>
      </div>
    </div>

    <div class="zoom-header flex justify-between items-center px-md">
      <div class="header-left flex items-center gap-md">
        <div class="text-caption text-grey-4 font-bold" style="font-size: 1.1rem; letter-spacing: 0.02em">
          {{ displayRoomId }}
        </div>

        <!-- Recording Status Indicator -->
        <div v-if="isRecording" class="recording-status flex items-center no-wrap">
          <span class="recording-pulse-dot"></span>
          <span class="text-red font-bold text-xs uppercase tracking-widest" style="font-size: 10px; line-height: 1">REC</span>
        </div>
        <div v-else-if="isStartingRecording" class="recording-status flex items-center no-wrap opacity-70">
          <q-spinner-dots color="red" size="14px" class="q-mr-xs" />
          <span class="text-red font-bold text-xs uppercase tracking-widest" style="font-size: 10px; line-height: 1">Starting...</span>
        </div>
      </div>

      <div class="header-right" style="width: 120px"></div>
    </div>

    <div class="main-stage">
      <div v-if="(activeScreenShare && activeScreenShare.track) || isWhiteboardActive" class="stage-layout">
        <div class="primary-view" :class="{'bg-white': isWhiteboardActive}">
          <video
            v-if="!isWhiteboardActive && activeScreenShare"
            :key="activeScreenShare.sid"
            :id="`video-${activeScreenShare.sid}`"
            :data-sid="activeScreenShare.sid"
            autoplay
            playsinline
            class="full-video object-contain"
            :ref="(el) => el && activeScreenShare && bindTrack(el, activeScreenShare.track)"></video>

          <canvas
            v-show="activeScreenShare || isWhiteboardActive"
            ref="annotationCanvasRef"
            class="annotation-canvas"
            :class="{'pointer-events-auto': isAnnotating || isWhiteboardActive, 'pointer-events-none': !(isAnnotating || isWhiteboardActive)}"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart.prevent="startDrawing"
            @touchmove.prevent="draw"
            @touchend="stopDrawing"
            @touchcancel="stopDrawing"></canvas>

          <div class="annotation-toolbar" v-if="(activeScreenShare && activeScreenShare.track) || isWhiteboardActive">
            <div class="tools-tray shadow-2">
              <template v-if="!isWhiteboardActive">
                <button class="tool-btn" :class="isAnnotating ? 'active' : ''" title="Annotate" @click="toggleAnnotation">
                  <span class="material-icons">draw</span>
                </button>
                <div v-if="isAnnotating && canDraw" class="separator-vertical mx-xs"></div>
              </template>

              <template v-if="(isAnnotating || isWhiteboardActive) && canDraw">
                <button class="tool-btn" :class="activeTool === 'pen' ? 'active' : ''" title="Pen" @click="activeTool = 'pen'">
                  <span class="material-icons">edit</span>
                </button>

                <button class="tool-btn" :class="activeTool === 'eraser' ? 'active' : ''" title="Eraser" @click="activeTool = 'eraser'">
                  <span class="material-icons">auto_fix_high</span>
                </button>

                <div class="color-picker-wrapper mx-xs">
                  <span class="material-icons color-picker-icon" :style="{color: currentColor, textShadow: '0 0 2px rgba(255,255,255,0.8)'}">palette</span>
                  <input type="color" v-model="currentColor" class="color-picker-input" title="Choose Color" />
                </div>

                <div class="separator-vertical mx-xs"></div>

                <button class="tool-btn" :disabled="strokeHistory.length === 0" title="Undo" @click="undoAnnotation">
                  <span class="material-icons" :class="strokeHistory.length === 0 ? 'text-grey-6' : ''">undo</span>
                </button>
                <button class="tool-btn" :disabled="redoStack.length === 0" title="Redo" @click="redoAnnotation">
                  <span class="material-icons" :class="redoStack.length === 0 ? 'text-grey-6' : ''">redo</span>
                </button>

                <div class="separator-vertical mx-xs"></div>

                <button class="tool-btn text-red" title="Clear All" @click="clearAnnotation">
                  <span class="material-icons">delete</span>
                </button>
              </template>

              <template v-if="isWhiteboardActive">
                <div v-if="canDraw" class="separator-vertical mx-xs"></div>
                <button class="tool-btn text-green" title="Export as Image" @click="exportWhiteboard">
                  <span class="material-icons">download</span>
                </button>
                <button
                  v-if="isTeacher"
                  class="tool-btn"
                  :class="studentsCanDraw ? 'active' : 'text-red'"
                  :title="studentsCanDraw ? 'Students can draw' : 'Only Teacher can draw'"
                  @click="toggleStudentDrawing">
                  <span class="material-icons">{{ studentsCanDraw ? 'group' : 'person_off' }}</span>
                </button>
              </template>
            </div>
          </div>

          <div class="tile-label" v-if="!isWhiteboardActive && activeScreenShare">{{ activeScreenShare.name }}'s Screen</div>
          <div class="tile-label" v-else-if="isWhiteboardActive">Collaborative Whiteboard</div>
        </div>

        <div class="participant-sidebar scroll">
          <div v-for="p in sidebarParticipants" :key="p.sid" class="sidebar-tile">
            <video
              :key="p.sid"
              :id="`video-${p.sid}`"
              :data-sid="p.sid"
              autoplay
              playsinline
              class="video-mirror"
              :muted="p.isLocal"
              :ref="(el) => el && bindTrack(el, p.videoTrack)"></video>
            <div class="reaction-overlay" v-if="activeReactions[p.sid] && activeReactions[p.sid].length">
              <transition-group name="fade-up">
                <div v-for="r in activeReactions[p.sid]" :key="r.id" class="reaction-emoji">
                  {{ r.emoji }}
                </div>
              </transition-group>
            </div>
            <div class="tile-label small-text">
              <span v-if="!p.isMicOn" class="material-icons text-red text-sm" style="vertical-align: middle">mic_off</span>
              {{ p.name }} {{ p.isLocal ? '(Me)' : '' }}
            </div>
          </div>
        </div>
      </div>

      <div v-else class="gallery-layout" :class="gridClass">
        <div v-for="p in participantsToDisplay" :key="p.sid" class="video-card" :class="{'speaker-active': p.sid === activeSpeakerSid}">
          <video
            :key="p.sid"
            :id="`video-${p.sid}`"
            :data-sid="p.sid"
            autoplay
            playsinline
            class="video-mirror"
            :muted="p.isLocal"
            :ref="(el) => el && bindTrack(el, p.videoTrack)"></video>
          <div class="reaction-overlay" v-if="activeReactions[p.sid] && activeReactions[p.sid].length">
            <transition-group name="fade-up">
              <div v-for="r in activeReactions[p.sid]" :key="r.id" class="reaction-emoji">
                {{ r.emoji }}
              </div>
            </transition-group>
          </div>
          <div class="tile-label">
            <span v-if="!p.isMicOn" class="material-icons text-red text-sm" style="vertical-align: middle">mic_off</span>
            <span>{{ p.name }} {{ p.isLocal ? '(Me)' : '' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="zoom-footer flex space-between items-center px-lg">
      <div class="footer-left flex items-center gap-md">
        <div class="control-unit" @click="toggleMic">
          <div class="icon-stack">
            <button class="btn btn-round" :class="isMicOn ? 'bg-transparent text-white' : 'bg-transparent text-red'">
              <span v-if="!isMicOn" class="material-icons">mic_off</span>
              <svg v-else viewBox="0 0 24 24" width="24" height="24" style="vertical-align: middle">
                <!-- Background of the capsule -->
                <path fill="rgba(255,255,255,0.3)" d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z" />
                <!-- Filling part -->
                <clipPath id="mic-fill-local-web">
                  <rect x="0" :y="14 - localAudioLevel * 12" width="24" :height="localAudioLevel * 12" />
                </clipPath>
                <path
                  fill="#2ecc71"
                  d="M12,14c1.66,0,3-1.34,3-3V5c0-1.66-1.34-3-3-3S9,3.34,9,5v6C9,12.66,10.34,14,12,14z"
                  clip-path="url(#mic-fill-local-web)" />
                <!-- The stand -->
                <path fill="currentColor" d="M17,11c0,2.76-2.24,5-5,5s-5-2.24-5-5H5c0,3.53,2.61,6.43,6,6.92V21h2v-3.08c3.39-0.49,6-3.39,6-6.92H17z" />
              </svg>
            </button>
            <span v-if="!hasMicPermission" class="material-icons permission-warning">report</span>
            <span class="material-icons dropdown-arrow text-sm">arrow_drop_up</span>
          </div>
          <div class="control-text">{{ !hasMicPermission ? 'Audio' : isMicOn ? 'Mute' : 'Unmute' }}</div>
        </div>
        <div class="control-unit">
          <div class="icon-stack">
            <button class="btn btn-round" :class="isVideoOn ? 'bg-transparent text-white' : 'bg-transparent text-red'" @click.stop="toggleVideo">
              <span class="material-icons">{{ isVideoOn ? 'videocam' : 'videocam_off' }}</span>
            </button>
            <span v-if="!hasCameraPermission" class="material-icons permission-warning">report</span>
            <span
              class="material-icons dropdown-arrow cursor-pointer"
              style="font-size: 20px !important; z-index: 5; padding: 6px; right: -16px; bottom: 2px"
              @click.stop="togglePanel('videoMenu')"
              >arrow_drop_up</span
            >
          </div>
          <div class="control-text cursor-pointer" @click.stop="toggleVideo">
            {{ !hasCameraPermission ? 'Video' : isVideoOn ? 'Stop Video' : 'Start Video' }}
          </div>

          <div v-if="isVideoMenuOpen" class="popover popover-top shadow-2" style="min-width: 200px" @click.stop>
            <ul class="dropdown-list">
              <li
                class="disabled-header text-grey-5 px-md py-sm"
                style="font-size: 11px; text-transform: uppercase; font-weight: bold; border-bottom: 1px solid #444">
                Video Options
              </li>

              <li v-if="isVideoOn" class="flex items-center gap-md px-md py-sm hover-white cursor-pointer" @click.stop="toggleBackgroundBlur">
                <span class="material-icons text-white">{{ backgroundEffect === 'blur' ? 'blur_off' : 'blur_on' }}</span>
                <span>{{ backgroundEffect === 'blur' ? 'Disable Blur' : 'Blur Background' }}</span>
              </li>

              <li v-if="isVideoOn" class="flex items-center gap-md px-md py-sm hover-white cursor-pointer" @click.stop="triggerBgImageUpload">
                <span class="material-icons text-white">image</span>
                <span>{{ backgroundImageUrl ? 'Change Picture' : 'Upload Picture' }}</span>
              </li>

              <li
                v-if="isVideoOn && backgroundImageUrl"
                class="flex items-center gap-md px-md py-sm hover-white cursor-pointer"
                @click.stop="toggleBackgroundImage">
                <span class="material-icons text-white">{{ backgroundEffect === 'image' ? 'visibility_off' : 'visibility' }}</span>
                <span>{{ backgroundEffect === 'image' ? 'Disable Picture' : 'Use Uploaded Picture' }}</span>
              </li>

              <li v-if="!isVideoOn" class="text-grey-5 px-md py-sm" style="font-size: 13px">Turn camera on to adjust background</li>
            </ul>
            <input type="file" ref="bgImageInput" accept="image/*" style="display: none" @change="onBgImageUpload" />
          </div>
        </div>
      </div>

      <div class="footer-center flex items-center gap-sm">
        <div class="control-unit" @click="togglePanel('participants')">
          <button class="btn btn-round bg-transparent text-white relative">
            <span class="material-icons">people</span>
            <span v-if="participants.length > 0" class="badge floating bg-blue">{{ participants.length }}</span>
          </button>
          <div class="control-text">Participants</div>
        </div>
        <div v-if="!isExtraSmall || !isMobile" class="control-unit" @click="togglePanel('chat')">
          <button class="btn btn-round bg-transparent text-white relative">
            <span class="material-icons">chat</span>
            <span
              v-if="hasUnreadMessages"
              class="badge floating bg-red"
              style="width: 10px; height: 10px; min-width: 0; padding: 0; border: 2px solid #1a1a1a; top: 2px; right: 2px"></span>
          </button>
          <div class="control-text">Chat</div>
        </div>

        <template v-if="!isMobile">
          <div v-if="!isIOS" class="control-unit" @click.stop="toggleScreenShare">
            <button class="btn btn-round bg-transparent" :class="isScreenSharing ? 'text-green' : 'text-white'">
              <span class="material-icons">{{ isScreenSharing ? 'stop_screen_share' : 'present_to_all' }}</span>
            </button>
            <div class="control-text">{{ isScreenSharing ? 'Stop Share' : 'Share' }}</div>
          </div>

          <div class="control-unit" @click="toggleWhiteboard">
            <button class="btn btn-round bg-transparent" :class="isWhiteboardActive ? 'text-green' : 'text-white'">
              <span class="material-icons">draw</span>
            </button>
            <div class="control-text">Whiteboard</div>
          </div>

          <div v-if="isTeacher" class="control-unit" @click.prevent="toggleRecord">
            <button class="btn btn-round bg-transparent" :class="isRecording ? 'text-red' : 'text-white'">
              <span class="material-icons" style="font-size: 28px">{{ isRecording ? 'stop_circle' : 'radio_button_checked' }}</span>
            </button>
            <div class="control-text flex items-center justify-center" :style="isRecording ? 'color: #f44336' : ''">
              <span v-if="isRecording" class="recording-pulse-dot" style="margin-right: 4px; width: 6px; height: 6px"></span>
              {{ isRecording ? 'Stop REC' : 'Record' }}
            </div>
          </div>

          <div class="control-unit" @click="togglePanel('breakout')">
            <button class="btn btn-round bg-transparent text-white">
              <span class="material-icons">grid_view</span>
            </button>
            <div class="control-text">Breakout</div>
          </div>

          <div class="control-unit relative">
            <button class="btn btn-round bg-transparent text-white" @click.stop="togglePanel('reactions')">
              <span class="material-icons">add_reaction</span>
            </button>
            <div v-if="isReactionsMenuOpen" class="popover popover-top shadow-2">
              <div class="flex p-sm gap-xs">
                <button class="btn btn-flat btn-round btn-sm" @click.stop="handleReaction('👍')">👍</button>
                <button class="btn btn-flat btn-round btn-sm" @click.stop="handleReaction('❤️')">❤️</button>
                <button class="btn btn-flat btn-round btn-sm" @click.stop="handleReaction('😂')">😂</button>
                <button class="btn btn-flat btn-round btn-sm" @click.stop="handleReaction('😮')">😮</button>
                <button class="btn btn-flat btn-round btn-sm" @click.stop="handleReaction('👏')">👏</button>
              </div>
            </div>
            <div class="control-text">React</div>
          </div>
        </template>
        <template v-else>
          <div class="control-unit relative">
            <button class="btn btn-round bg-transparent text-white" @click.stop="togglePanel('more')">
              <span class="material-icons">more_horiz</span>
            </button>
            <div v-if="isMoreMenuOpen" class="popover popover-top shadow-2" style="min-width: 220px">
              <ul class="dropdown-list">
                <template v-if="!isMobile || !isReactionsMenuOpen">
                  <li v-if="isExtraSmall && isMobile" @click="togglePanel('chat')" class="relative">
                    <span class="material-icons" :class="hasUnreadMessages ? 'text-red' : 'text-white'">chat</span>
                    <span>Chat</span>
                    <span v-if="hasUnreadMessages" class="badge bg-red" style="top: 8px; left: 28px"></span>
                  </li>
                  <li v-if="!isIOS" @click.stop="handleMoreScreenShare">
                    <span class="material-icons" :class="isScreenSharing ? 'text-green' : 'text-white'">{{
                      isScreenSharing ? 'stop_screen_share' : 'present_to_all'
                    }}</span>
                    <span>{{ isScreenSharing ? 'Stop Share' : 'Share Screen' }}</span>
                  </li>
                  <li @click="handleMoreWhiteboard">
                    <span class="material-icons" :class="isWhiteboardActive ? 'text-green' : 'text-white'">draw</span>
                    <span>Whiteboard</span>
                  </li>
                  <li v-if="isTeacher" @click="handleMoreRecord">
                    <span class="material-icons" :class="isRecording ? 'text-red' : 'text-white'">{{
                      isRecording ? 'stop_circle' : 'radio_button_checked'
                    }}</span>
                    <span>{{ isRecording ? 'Stop Recording' : 'Start Recording' }}</span>
                  </li>
                  <li @click="handleMoreBreakout">
                    <span class="material-icons">grid_view</span>
                    <span>Breakout Rooms</span>
                  </li>
                  <li class="flex items-center gap-md px-md py-sm hover-white cursor-pointer w-full" @click.stop="isReactionsMenuOpen = true">
                    <span class="material-icons">add_reaction</span>
                    <span>Reactions</span>
                    <span class="material-icons text-xs ml-auto">chevron_right</span>
                  </li>
                </template>
                <template v-else>
                  <li class="flex-col items-stretch p-0 no-hover">
                    <div class="flex items-center space-between px-md py-sm border-bottom">
                      <span class="font-bold text-sm">Select Reaction</span>
                      <button class="btn btn-flat btn-round btn-xs text-white" @click.stop="isReactionsMenuOpen = false">
                        <span class="material-icons text-sm">close</span>
                      </button>
                    </div>
                    <div class="p-sm flex gap-xs justify-center flex-wrap">
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('👍')">👍</button>
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('❤️')">❤️</button>
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('😂')">😂</button>
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('😮')">😮</button>
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('👏')">👏</button>
                      <button class="btn btn-flat btn-round text-md" @click.stop="handleMoreReaction('🎉')">🎉</button>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
            <div class="control-text">More</div>
          </div>
        </template>
      </div>

      <div class="footer-right relative">
        <button class="btn btn-red leave-button px-md font-bold" @click.stop="togglePanel('leave')">Leave</button>
        <div v-if="isLeaveMenuOpen" class="popover popover-top-right shadow-2" style="min-width: 200px">
          <ul class="dropdown-list">
            <li v-if="isInBreakoutRoom" @click="handleLeaveBreakout">
              <span class="material-icons text-white">meeting_room</span>
              <span>Leave Breakout Room</span>
            </li>
            <li @click="handleLeaveMeeting">
              <span class="material-icons text-white">logout</span>
              <span>{{ isTeacher ? 'End Meeting for All' : 'Leave Meeting' }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <transition name="slide-chat">
      <div v-if="isChatOpen" class="chat-panel">
        <div class="chat-header">
          <div class="chat-title">
            <span class="material-icons text-sm mr-xs">chat</span>
            In-Meeting Chat
          </div>
          <button class="btn btn-flat btn-round btn-sm text-white" @click="isChatOpen = false">
            <span class="material-icons">close</span>
          </button>
        </div>

        <div class="chat-messages" ref="chatScrollRef">
          <div v-if="chatMessages.length === 0" class="chat-empty">
            <span class="material-icons text-grey-7" style="font-size: 40px">forum</span>
            <div class="text-grey-6 text-caption mt-sm">No messages yet</div>
          </div>
          <div v-for="(msg, i) in chatMessages" :key="i" class="chat-bubble" :class="msg.isMe ? 'bubble-me' : 'bubble-them'">
            <div class="bubble-sender">{{ msg.isMe ? 'You' : msg.sender }}</div>
            <div class="bubble-text" v-html="formatMessage(msg.text)"></div>
            <div class="bubble-time">{{ msg.time }}</div>
          </div>
        </div>

        <div class="chat-input-area">
          <div class="input-container">
            <input type="text" v-model="chatInput" placeholder="Type a message..." class="chat-input" @keyup.enter="sendMessage" />
            <button class="btn btn-flat btn-round btn-sm text-primary absolute-right" @click="sendMessage" :disabled="!chatInput.trim()">
              <span class="material-icons">send</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="slide-chat">
      <div v-if="isParticipantsOpen" class="participants-panel">
        <div class="chat-header">
          <div class="chat-title">
            <span class="material-icons text-sm mr-xs">people</span>
            Participants ({{ participants.length }})
          </div>
          <button class="btn btn-flat btn-round btn-sm text-white" @click="isParticipantsOpen = false">
            <span class="material-icons">close</span>
          </button>
        </div>

        <div class="participants-list scroll">
          <ul class="member-list">
            <li v-for="p in participants" :key="p.sid" class="member-item flex space-between items-center py-md px-md">
              <div class="flex items-center gap-md">
                <div class="avatar text-white" :style="{backgroundColor: getAvatarColor(p.name)}">
                  {{ (p.name || 'U')[0].toUpperCase() }}
                </div>
                <div class="flex column">
                  <span class="text-white font-bold" style="font-size: 14px"> {{ p.name }} {{ p.isLocal ? '(Me)' : '' }} </span>
                  <span class="text-grey-5 text-sm" style="font-size: 12px; margin-top: 2px">
                    {{ p.isLocal ? 'Host' : 'Participant' }}
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-md">
                <div class="flex items-center gap-sm">
                  <span class="material-icons text-sm icon-status" :class="p.isMicOn ? 'text-grey-4' : 'text-red'">
                    {{ p.isMicOn ? 'mic' : 'mic_off' }}
                  </span>
                  <span class="material-icons text-sm icon-status" :class="p.isVideoOn ? 'text-grey-4' : 'text-red'">
                    {{ p.isVideoOn ? 'videocam' : 'videocam_off' }}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </transition>

    <mic-permission-modal
      :permanently-denied="mediaPermissionPermanentlyDenied"
      :visible="showMediaPermissionModal"
      :type="mediaPermissionType"
      @close="showMediaPermissionModal = false"
      @use-media="retryMediaPermission" />
  </div>
</template>

<script>
import {Room, RoomEvent, VideoPresets, Track} from 'livekit-client'
import MicPermissionModal from './MicPermissionModal.vue'

const App = window.App

export default {
  name: 'LiveKitRoom',
  components: {
    MicPermissionModal,
  },

  data() {
    return {
      toasts: [],
      toastId: 0,
      isMobile: window.innerWidth < 1024,
      isJoinDialogOpen: true,
      isConnecting: false,
      micPermanentlyDenied: false, // Legacy, keeping for now or renaming if safe
      mediaPermissionPermanentlyDenied: false,
      mediaPermissionType: 'mic',
      showMediaPermissionModal: false,
      classSid: this.$route.query.sid || '',
      roomId: `MainRoom_${this.classSid}`,
      userName: this.$route.query.name || '',
      isTeacher: this.$route.query.role !== 'student',
      isRoomsDashboardOpen: false,
      expandedRooms: {},
      allRoomAssignments: {},
      isMicOn: true,
      isVideoOn: true,
      hasMicPermission: true,
      hasCameraPermission: true,
      isScreenSharing: false,
      isChatOpen: false,
      isParticipantsOpen: false,
      isRecording: false,
      isStartingRecording: false,
      ownedRecording: false,

      isLeaveMenuOpen: false,
      isMoreMenuOpen: false,
      isReactionsMenuOpen: false,
      isVideoMenuOpen: false,
      backgroundEffect: 'none',
      backgroundImageUrl: '',
      blurProcessor: null,
      hasUnreadMessages: false,
      moveMenuState: {},
      activeSpeakerSid: null,
      localAudioLevel: 0,
      audioLevelInterval: null,
      chatMessages: [],
      chatInput: '',
      activeReactions: {},
      isAnnotating: false,
      isWhiteboardActive: false,
      studentsCanDraw: true,
      strokeHistory: [],
      redoStack: [],
      activeTool: 'pen',
      currentColor: '#000000',
      participants: [],
      room: null,
      currentStrokeId: null,
      isDrawing: false,
      drawingCtx: null,
      assignedListener: null,
      meetingEndedListener: null,
      recordingStartedListener: null,
      recordingStoppedListener: null,
      recordingErrorListener: null,

      gameOnPatchedListener: null,
      participantAssignMenuState: {},
      gameOnRecord: null,
      roomsLoadingLabel: '',
      moveMenuStyles: {},
      heartbeatInterval: null,
      sessionId: this.$route.query.session_id || '',
    }
  },

  computed: {
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    },
    isExtraSmall() {
      return window.innerWidth < 540
    },
    canDraw() {
      return this.isTeacher || this.studentsCanDraw
    },
    isInBreakoutRoom() {
      return !this.isMainRoomName(this.roomId)
    },
    displayRoomId() {
      if (!this.roomId) return ''
      return this.roomId.replace(new RegExp(`^${this.classSid}__`), '')
    },
    isAllExpanded() {
      const roomKeys = Object.keys(this.displayRooms)
      if (roomKeys.length === 0) return false
      return roomKeys.every((key) => this.expandedRooms[key])
    },
    isAllCollapsed() {
      const roomKeys = Object.keys(this.displayRooms)
      if (roomKeys.length === 0) return true
      return roomKeys.every((key) => !this.expandedRooms[key])
    },
    statusLabel() {
      if (this.room && this.room.state === 'connected') return this.isTeacher ? 'Teacher (Main)' : `Student (${this.displayRoomId})`
      return 'Disconnected'
    },
    activeScreenShare() {
      const sharer = this.participants.find((p) => p.isScreenSharing && p.screenTrack)
      if (!sharer) return null
      return {sid: sharer.sid, name: sharer.name, track: sharer.screenTrack}
    },
    participantsToDisplay() {
      return [...this.participants].sort((a, b) => {
        if (a.sid === this.activeSpeakerSid) return -1
        if (b.sid === this.activeSpeakerSid) return 1
        if (!a.name || !b.name) return 0
        return a.name.localeCompare(b.name)
      })
    },
    sidebarParticipants() {
      return this.participants
    },
    gridClass() {
      const count = this.participantsToDisplay.length
      if (count <= 1) return 'grid-1'
      if (count <= 2) return 'grid-2'
      if (count <= 4) return 'grid-4'
      if (count <= 6) return 'grid-6'
      return 'grid-many'
    },
    filteredRoomAssignments() {
      const res = {...(this.allRoomAssignments || {})}
      const assignedNames = new Set()
      // Normalized MainRoom keys (merge mainroom/Main Session into MainRoom)
      const isMainRoom = (name) => this.isMainRoomName(name)

      const getRoomTitle = (roomName) => {
        if (isMainRoom(roomName)) return 'Main Session'
        return roomName
      }

      // Build a set of names currently in breakout rooms
      Object.entries(res).forEach(([roomName, list]) => {
        if (getRoomTitle(roomName) === 'Main Session') return
        ;(list || []).forEach((m) => {
          if (m && m.userName) {
            assignedNames.add(m.userName.replace(' (Me)', '').trim())
          }
        })
      })

      // Normalize MainRoom keys (merge mainroom/Main Session into MainRoom)
      if (!res['MainRoom']) this.$set(res, 'MainRoom', [])
      if (res['mainroom']) {
        res['MainRoom'] = [...res['MainRoom'], ...res['mainroom']]
        delete res['mainroom']
      }
      if (res['Main Session']) {
        res['MainRoom'] = [...res['MainRoom'], ...res['Main Session']]
        delete res['Main Session']
      }

      res['MainRoom'] = res['MainRoom'].filter((m) => !assignedNames.has((m.userName || '').replace(' (Me)', '').trim()))

      if (this.participants && this.participants.length) {
        this.participants.forEach((p) => {
          const cleanName = (p.name || '').replace(' (Me)', '').trim()
          // 🛡️ Only add from physical room if they are NOT assigned ANYWHERE in the server state
          if (!assignedNames.has(cleanName)) {
            let currentRoomKey = this.roomId || 'MainRoom'
            if (isMainRoom(currentRoomKey)) {
              currentRoomKey = 'MainRoom'
            }

            if (!res[currentRoomKey]) this.$set(res, currentRoomKey, [])

            if (!res[currentRoomKey].some((m) => (m.userName || '').replace(' (Me)', '').trim() === cleanName)) {
              res[currentRoomKey].push({userName: p.name || p.identity, roomName: currentRoomKey})
            }
          }
        })
      }

      if (!this.isTeacher) {
        delete res['MainRoom']
        delete res['mainroom']
      }

      return res
    },
    displayRooms() {
      const rooms = {}

      if (this.gameOnRecord && this.gameOnRecord.teams && this.gameOnRecord.teams.length) {
        for (const team of this.gameOnRecord.teams) {
          const key = `Team-${team.id}`
          rooms[key] = this.allRoomAssignments[key] || []
        }
      }

      for (const [key, members] of Object.entries(this.filteredRoomAssignments)) {
        if (!rooms[key] || rooms[key].length === 0) rooms[key] = members
      }
      console.log('jkhkhkjhj', this.gameOnRecord, 'allRoomAssignments', this.allRoomAssignments)
      return rooms
    },
  },

  watch: {
    activeScreenShare(val) {
      if (val || this.isWhiteboardActive) {
        this.$nextTick(() => {
          this.resizeCanvas()
        })
      } else {
        this.isAnnotating = false
        this.strokeHistory = []
        this.redoStack = []
        this.drawingCtx = null
        this.$nextTick(() => this.redrawCanvas())
      }
    },
    isWhiteboardActive(val) {
      if (val || this.activeScreenShare) {
        this.$nextTick(() => {
          this.resizeCanvas()
          if (val) {
            const canvas = this.$refs.annotationCanvasRef
            if (canvas) {
              this.drawingCtx = canvas.getContext('2d')
              if (this.drawingCtx) {
                this.drawingCtx.lineCap = 'round'
                this.drawingCtx.lineJoin = 'round'
              }
            }
          }
        })
      } else {
        this.isAnnotating = false
        this.strokeHistory = []
        this.redoStack = []
        this.drawingCtx = null
        this.$nextTick(() => this.redrawCanvas())
      }
    },
  },

  async mounted() {
    window.addEventListener('resize', this.updateWindowSize)
    const findBreakoutroom = await App.service('breakout-rooms').find({query: {sid: this.classSid}})

    if (findBreakoutroom) {
      Object.entries(findBreakoutroom).forEach(([roomName, participants]) => {
        participants.forEach((p) => {
          this.syncDashboardState(p.userName, roomName)
        })
      })
    }

    App.service('breakout-rooms').on('created', (data) => {
      if (data.sid === this.classSid) {
        const assignments = data.assignments || (data.userName ? [data] : [])
        assignments.forEach((asgn) => {
          this.syncDashboardState(asgn.userName, asgn.roomName)

          const nameMatch = asgn.userName === this.userName
          const uidMatch = asgn.uid && window.currentUserId && asgn.uid === window.currentUserId
          const isForMe = uidMatch || (nameMatch && !asgn.uid)

          if (isForMe && this.displayRoomId !== asgn.roomName && this.roomId !== asgn.roomName) {
            this.joinBreakoutRoom(asgn.roomName)
          }
        })
      }
    })

    this.assignedListener = async (data) => {
      if (data.sid === this.classSid) {
        const assignments = data.assignments || (data.userName ? [data] : [])
        assignments.forEach((asgn) => {
          this.syncDashboardState(asgn.userName, asgn.roomName)

          const nameMatch = asgn.userName === this.userName
          const uidMatch = asgn.uid && window.currentUserId && asgn.uid === window.currentUserId
          const isForMe = uidMatch || (nameMatch && !asgn.uid)

          if (isForMe && this.displayRoomId !== asgn.roomName && this.roomId !== asgn.roomName) {
            this.joinBreakoutRoom(asgn.roomName)
          }
        })

        const latestRooms = await App.service('breakout-rooms').find({query: {sid: this.classSid}})
        if (latestRooms) {
          Object.entries(latestRooms).forEach(([roomName, participants]) => {
            participants.forEach((p) => {
              this.syncDashboardState(p.userName, roomName)
            })
          })
        }

        this.refreshAssignments()
      }
    }

    App.service('breakout-rooms').on('assigned', this.assignedListener)

    this.meetingEndedListener = async () => {
      await this.handleLeave()
    }
    App.service('breakout-rooms').on('meeting-ended', this.meetingEndedListener)

    if (App && App.service('livekit-recording')) {
      this.recordingStartedListener = (data) => {
        const scopedRoom = this.roomId.includes(this.classSid) ? this.roomId : `${this.classSid}__${this.roomId}`
        console.log('[RECORDING] Received recording-started event:', data, 'Target:', scopedRoom)

        if (data.roomName !== this.roomId && data.roomName !== scopedRoom) {
          console.warn('[RECORDING] Room mismatch:', data.roomName, 'is neither', this.roomId, 'nor', scopedRoom)
          return
        }
        console.log('[RECORDING] Match found! Started for room:', data.roomName)
        this.isRecording = true
        this.isStartingRecording = false
        if (!this.isTeacher) {
          this.showToast({
            message: '🔴 Recording started',
            type: 'negative',
            timeout: 3000,
          })
        }

        // ★ Re-broadcast whiteboard state so recorder-bot captures pre-existing whiteboard
        if (this.isTeacher && this.isWhiteboardActive && this.room && this.room.state === 'connected') {
          const self = this
          const broadcastWBState = () => {
            if (!self.isWhiteboardActive || !self.room || self.room.state !== 'connected') return
            console.log('[RECORDING] Broadcasting whiteboard state to recorder-bot')
            self.sendWhiteboardData({action: 'start'})
            if (self.strokeHistory.length > 0) {
              self.sendWhiteboardData({action: 'sync_history', history: self.strokeHistory, studentsCanDraw: self.studentsCanDraw})
            } else {
              self.sendWhiteboardData({action: 'allow_draw', value: self.studentsCanDraw})
            }
          }
          // Staggered retries: recorder needs time to set up its DataReceived handler
          setTimeout(broadcastWBState, 1000)
          setTimeout(broadcastWBState, 3000)
          setTimeout(broadcastWBState, 5000)
        }
      }
      this.recordingStoppedListener = (data) => {
        try {
          const scopedRoom = this.roomId.includes(this.classSid) ? this.roomId : `${this.classSid}__${this.roomId}`
          console.log('[RECORDING] Received recording-stopped event:', data, 'Target:', scopedRoom)

          if (data.roomName !== this.roomId && data.roomName !== scopedRoom) {
            console.warn('[RECORDING] Room mismatch:', data.roomName, 'is neither', this.roomId, 'nor', scopedRoom)
            return
          }
          console.log('[RECORDING] Match found! Stopped for room:', data.roomName, '→', data.downloadUrl)
          this.isRecording = false

          if (this.isTeacher && data.downloadUrl) {
            // Auto-download the recording for the teacher
            window.open(data.downloadUrl, '_blank')
            this.showToast({
              type: 'positive',
              message: '✅ Recording saved! Download started in a new tab.',
              timeout: 5000,
            })

            // Delete the recording from S3 after a short delay to ensure download starts
            setTimeout(() => {
              App.service('livekit-recording')
                .patch(null, {
                  action: 'delete-s3',
                  downloadUrl: data.downloadUrl,
                  roomName: data.roomName,
                  sid: this.classSid,
                })
                .then(() => {
                  console.log('[RECORDING] S3 file deletion requested successfully')
                })
                .catch((err) => {
                  console.warn('[RECORDING] Failed to delete S3 file:', err.message)
                })
            }, 5000)
          }
        } catch (err) {
          console.error('[RECORDING] Error processing recording-stopped event:', err)
          this.isRecording = false
        }
        this.ownedRecording = false
      }
      this.recordingErrorListener = (data) => {
        const scopedRoom = this.roomId.includes(this.classSid) ? this.roomId : `${this.classSid}__${this.roomId}`
        console.log('[RECORDING] Received recording-error event:', data, 'Target:', scopedRoom)

        if (data.roomName !== this.roomId && data.roomName !== scopedRoom) {
          console.warn('[RECORDING] Room mismatch:', data.roomName, 'is neither', this.roomId, 'nor', scopedRoom)
          return
        }

        console.error('[RECORDING] Error:', data.message)
        this.isRecording = false
        this.ownedRecording = false
        this.isStartingRecording = false

        this.showToast({
          type: 'negative',
          message: `Recording Error: ${data.message || 'Failed to start/stop recording.'}`,
          timeout: 10000,
        })
      }
      App.service('livekit-recording').on('recording-started', this.recordingStartedListener)
      App.service('livekit-recording').on('recording-stopped', this.recordingStoppedListener)
      App.service('livekit-recording').on('recording-error', this.recordingErrorListener)
    }

    if (App && App.service('game-on') && this.classSid) {
      this.gameOnPatchedListener = async (data) => {
        if (data.sid !== this.classSid) return

        if (this.gameOnRecord) {
          const currentId = this.gameOnRecord._id || this.gameOnRecord.id
          const newId = data._id || data.id
          const currentCreated = new Date(this.gameOnRecord.createdAt).getTime()
          const newCreated = new Date(data.createdAt).getTime()

          if (newId !== currentId && newCreated < currentCreated) {
            return
          }
        }

        this.gameOnRecord = data
        this.refreshAssignments()
        if (!this.isTeacher) {
          if (data.status === 1) {
            await this.autoJoinStudentTeamRoom(data)
          } else {
            if (this.isInBreakoutRoom) {
              await this.joinBreakoutRoom('MainRoom')
              this.showToast({message: 'Game ended — returning to Main Room', type: 'info'})
            }
          }
        }
      }

      this.gameOnCreatedListener = async (data) => {
        if (data.sid !== this.classSid) return

        if (this.gameOnRecord) {
          const currentCreated = new Date(this.gameOnRecord.createdAt).getTime()
          const newCreated = new Date(data.createdAt).getTime()
          if (newCreated < currentCreated) {
            return
          }
        }

        this.gameOnRecord = data
        this.refreshAssignments()
        if (!this.isTeacher && data.status === 1) {
          await this.autoJoinStudentTeamRoom(data)
        }
      }

      App.service('game-on').on('patched', this.gameOnPatchedListener)
      App.service('game-on').on('created', this.gameOnCreatedListener)

      window.addEventListener(
        'scroll',
        () => {
          this.moveMenuState = {}
        },
        true
      )
    }

    if (App && App.service('auth')) {
      this.authPatchedListener = async (payload) => {
        if (payload.commit === 'student/startGameOn' && payload.data) {
          const record = {...payload.data, status: 1}

          if (this.gameOnRecord) {
            const currentCreated = new Date(this.gameOnRecord.createdAt).getTime()
            const newCreated = new Date(record.createdAt).getTime()
            if (newCreated < currentCreated) {
              return
            }
          }

          if (record.teams) {
            record.members = record.teams.flatMap((t) =>
              (t.members || []).map((m) => ({
                uid: m.originalMemberData?.uid || m.uid || (typeof m === 'string' ? m : ''),
                name: m.originalMemberData?.name || m.name || (typeof m === 'string' ? '' : ''),
                teamId: t.id,
              }))
            )
          }

          this.gameOnRecord = record
          if (!this.isTeacher) {
            await this.autoJoinStudentTeamRoom(record)
          }
        } else if (payload.commit === 'student/endGameOn') {
          if (!this.isTeacher && this.isInBreakoutRoom) {
            await this.joinBreakoutRoom('MainRoom')
            this.showToast({message: 'Game ended — returning to Main Room', type: 'info'})
          }
        } else if (payload.commit === 'student/notifyJoinTeacherRoom') {
          const currentUid = this.$route.query.uid || window.currentUserId || ''
          if (payload.data?.uid === currentUid) {
            await this.joinBreakoutRoom('MainRoom')
          }
        }
      }
      App.service('auth').on('patched', this.authPatchedListener)
    }

    if (this.isTeacher && App) {
      try {
        const token = this.$route.query.token || (await App.authentication.getAccessToken()) || ''
        if (!token) {
          return this.goToLogin()
        }
        const authResult = await App.authenticate({strategy: 'jwt', accessToken: token}).catch(() => null)
        if (!authResult || !authResult.user) {
          return this.goToLogin()
        }
        const user = authResult.user
        if (!this.userName) {
          this.userName = user.nickname || user.name || user.email || ''
        }
      } catch (err) {
        this.goToLogin()
      }
    }

    this.startHeartbeat()
    window.addEventListener('beforeunload', this.handleTabClose)
  },

  beforeDestroy() {
    this.stopHeartbeat()
    window.removeEventListener('beforeunload', this.handleTabClose)
    this.stopAudioLevelPolling()
    window.removeEventListener('resize', this.updateWindowSize)
    if (this.room) {
      this.room.disconnect()
    }

    if (App && App.service('breakout-rooms')) {
      if (this.assignedListener) App.service('breakout-rooms').removeListener('assigned', this.assignedListener)
      if (this.meetingEndedListener) App.service('breakout-rooms').removeListener('meeting-ended', this.meetingEndedListener)
    }

    if (App && App.service('game-on') && this.gameOnPatchedListener) {
      App.service('game-on').removeListener('patched', this.gameOnPatchedListener)
    }

    if (App && App.service('livekit-recording')) {
      if (this.recordingStartedListener) App.service('livekit-recording').removeListener('recording-started', this.recordingStartedListener)
      if (this.recordingStoppedListener) App.service('livekit-recording').removeListener('recording-stopped', this.recordingStoppedListener)
      if (this.recordingErrorListener) App.service('livekit-recording').removeListener('recording-error', this.recordingErrorListener)
    }
  },

  methods: {
    isMainRoomName(name) {
      if (!name) return false
      const clean = name.toLowerCase()
      return (
        clean === 'mainroom' || clean === 'main session' || (this.classSid && (name === `MainRoom_${this.classSid}` || name === `${this.classSid}__MainRoom`))
      )
    },
    goToLogin() {
      const back = encodeURIComponent(location.href)
      const loginUrl = (window.hostUrl || '') + '/v2/login?from=roomTeacher&role=teacher&back=' + back
      location.href = loginUrl
    },
    showToast(options) {
      const message = typeof options === 'string' ? options : options.message
      const type = options.type || (options.color && options.color.includes('red') ? 'negative' : 'info')
      const id = ++this.toastId
      this.toasts.push({id, message, type})
      setTimeout(() => {
        this.toasts = this.toasts.filter((t) => t.id !== id)
      }, options.timeout || 3000)
    },
    updateWindowSize() {
      this.isMobile = window.innerWidth < 1024
      this.resizeCanvas()
    },
    expandAll() {
      Object.keys(this.displayRooms).forEach((key) => {
        this.$set(this.expandedRooms, key, true)
      })
    },
    collapseAll() {
      Object.keys(this.displayRooms).forEach((key) => {
        this.$set(this.expandedRooms, key, false)
      })
    },
    async connectToRoom(targetRoomId, token) {
      if (this.isConnecting) return
      this.isConnecting = true

      try {
        if (this.room) {
          await this.room.disconnect()
          await new Promise((resolve) => setTimeout(resolve, 500))
          this.participants = []
          this.isScreenSharing = false
        }

        this.roomId = targetRoomId

        this.room = new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
          },
          publishDefaults: {
            dtx: true,
            videoSimulcastLayers: [VideoPresets.h720, VideoPresets.h360],
            screenShareSimulcastLayers: [VideoPresets.h1080, VideoPresets.h720],
          },
          videoCaptureDefaults: {
            resolution: VideoPresets.h720.resolution,
          },
        })

        this.setupRoomListeners(this.room)

        const url = 'wss://dev-meeting.classcipe.com'
        await this.room.connect(url, token, {
          autoSubscribe: true,
          publishDefaults: {
            simulcast: true,
            videoCaptureDefaults: {
              resolution: VideoPresets.h360.resolution,
            },
          },
        })

        this.room.remoteParticipants.forEach((p) => {
          this.updateParticipantState(p)
        })

        this.isJoinDialogOpen = false

        try {
          if (this.isMicOn) {
            await this.room.localParticipant.setMicrophoneEnabled(true)
            this.hasMicPermission = true
          }
          if (this.isVideoOn) {
            await this.room.localParticipant.setCameraEnabled(true)
            this.hasCameraPermission = true
          }
        } catch (mediaErr) {
          if (mediaErr.name === 'NotAllowedError' || mediaErr.name === 'PermissionDeniedError') {
            if (this.isMicOn) this.hasMicPermission = false
            if (this.isVideoOn) this.hasCameraPermission = false
          }
          this.showToast({type: 'warning', message: 'Could not access Camera or Mic automatically.'})
        }

        this.updateParticipantState(this.room.localParticipant)
        this.room.startAudio().catch(() => {})
        this.startAudioLevelPolling()
      } catch (err) {
        this.showToast({type: 'negative', message: 'Failed to connect.'})
      } finally {
        this.isConnecting = false
      }
    },
    async joinBreakoutRoom(targetRoomName) {
      if (this.isConnecting || this.displayRoomId === targetRoomName || this.roomId === targetRoomName) return
      try {
        const response = await App.service('livekit').create({
          roomName: targetRoomName,
          participantName: this.userName,
          sid: this.classSid,
        })
        const token = typeof response === 'string' ? response : response.token
        const actualRoom = response.roomName || targetRoomName
        this.roomId = actualRoom
        await this.connectToRoom(actualRoom, token)
        this.broadcastSelfAssignment(targetRoomName)
      } catch (err) {}
    },
    handleCancel() {
      window.parent.postMessage('livekit-leave', '*')
      window.close()
    },
    handleTabClose() {
      if (this.isTeacher && this.room && this.room.state === 'connected') {
        // Fire-and-forget signals to students and server
        App.service('auth')
          .patch('syncTeacher', {
            commit: 'teacher/setMeetingTeacherJoined',
            data: false,
          })
          .catch(() => {})

        App.service('session')
          .get(this.classSid)
          .then((session) => {
            App.service('session')
              .patch(this.classSid, {
                zoom: {
                  ...(session.zoom || {}),
                  teacherJoined: false,
                },
              })
              .catch(() => {})
          })
          .catch(() => {})
      }
      this.stopHeartbeat()
    },
    async loadGameOnRecord(force = false) {
      if (this.gameOnRecord && !force) return this.gameOnRecord
      const sid = this.classSid
      if (!sid) return null
      try {
        const res = await App.service('game-on').find({
          query: {sid, $sort: {createdAt: -1}, $limit: 1},
        })
        if (res.data && res.data.length > 0) {
          this.gameOnRecord = res.data[0]
        } else {
        }
      } catch (err) {}
      return this.gameOnRecord
    },

    async autoJoinStudentTeamRoom(gameOnDoc) {
      if (this.isConnecting) {
        return
      }
      const targetRoom = this.resolveStudentRoom(gameOnDoc)
      if (targetRoom !== 'MainRoom' && this.roomId !== targetRoom) {
        this.showToast({message: `Game started — joining ${targetRoom.replace('-', ' ')}`, type: 'info'})
        await this.joinBreakoutRoom(targetRoom)
      }
    },
    resolveStudentRoom(gameOnDoc) {
      if (!gameOnDoc || !gameOnDoc.teams || (!gameOnDoc.members && !gameOnDoc.teams[0]?.members)) return 'MainRoom'
      if (gameOnDoc.status !== 1) return 'MainRoom'

      const searchName = this.userName.trim().toLowerCase()
      const searchUid = window.currentUserId || ''

      const membersSource =
        gameOnDoc.members ||
        gameOnDoc.teams.flatMap((t) =>
          (t.members || []).map((m) => ({
            uid: m.originalMemberData?.uid || m.uid,
            name: m.originalMemberData?.name || m.name,
            teamId: t.id,
          }))
        )

      const match = membersSource.find((m) => (m.uid && m.uid === searchUid) || (m.name || '').trim().toLowerCase() === searchName)

      return match ? `Team-${match.teamId}` : 'MainRoom'
    },
    async handleJoin() {
      if (!this.userName.trim() || this.isConnecting) return

      try {
        await this.loadGameOnRecord(true)

        let targetRoom = 'MainRoom'
        if (!this.isTeacher && this.gameOnRecord) {
          targetRoom = this.resolveStudentRoom(this.gameOnRecord)
        }

        this.roomId = targetRoom

        if (this.isTeacher) {
          const response = await App.service('livekit').create({
            roomName: targetRoom,
            participantName: this.userName,
            sid: this.classSid,
          })
          const token = typeof response === 'string' ? response : response.token
          const actualRoom = response.roomName || targetRoom
          this.roomId = actualRoom
          await this.connectToRoom(actualRoom, token)
          this.broadcastSelfAssignment(targetRoom)
          await this.refreshAssignments()

          // Signal to students that the teacher has joined the meeting
          try {
            const session = await App.service('session').get(this.classSid)
            await App.service('session').patch(this.classSid, {
              zoom: {
                ...(session.zoom || {}),
                teacherJoined: true,
              },
            })

            // Broadcast to students in real-time via auth service
            await App.service('auth').patch('syncTeacher', {
              commit: 'teacher/setMeetingTeacherJoined',
              data: true,
            })
          } catch (patchErr) {
            console.error('[MEETING] Failed to patch session with teacherJoined:', patchErr)
          }
        } else {
          const response = await App.service('livekit').create({
            roomName: targetRoom,
            participantName: this.userName,
            sid: this.classSid,
          })
          const token = typeof response === 'string' ? response : response.token
          const actualRoom = response.roomName || targetRoom
          this.roomId = actualRoom
          await this.connectToRoom(actualRoom, token)
          this.broadcastSelfAssignment(targetRoom)

          await this.refreshAssignments()

          if (this.gameOnRecord && this.gameOnRecord.status === 1) {
            await this.autoJoinStudentTeamRoom(this.gameOnRecord)
          }
        }

        // Fetch initial recording status
        try {
          const scopedRoom = this.roomId.includes(this.classSid) ? this.roomId : `${this.classSid}__${this.roomId}`
          const recStatus = await App.service('livekit-recording').find({query: {roomName: scopedRoom, sid: this.classSid}})
          this.isRecording = !!recStatus.isRecording
          console.log('[RECORDING] Initial status fetch for:', scopedRoom, '->', this.isRecording)
        } catch (err) {
          console.warn('[RECORDING] Failed to fetch initial status:', err)
        }

        this.isJoinDialogOpen = false
      } catch (err) {}
    },
    togglePanel(panel) {
      const nextChat = panel === 'chat' ? !this.isChatOpen : false
      const nextParticipants = panel === 'participants' ? !this.isParticipantsOpen : false
      const nextBreakout = panel === 'breakout' ? !this.isRoomsDashboardOpen : false
      const nextReactions = panel === 'reactions' ? !this.isReactionsMenuOpen : false
      const nextMore = panel === 'more' ? !this.isMoreMenuOpen : false
      const nextLeave = panel === 'leave' ? !this.isLeaveMenuOpen : false
      const nextVideoMenu = panel === 'videoMenu' ? !this.isVideoMenuOpen : false

      this.isChatOpen = nextChat
      this.isParticipantsOpen = nextParticipants
      this.isRoomsDashboardOpen = nextBreakout
      this.isReactionsMenuOpen = nextReactions
      this.isMoreMenuOpen = nextMore
      this.isLeaveMenuOpen = nextLeave
      this.isVideoMenuOpen = nextVideoMenu

      if (nextChat) this.hasUnreadMessages = false
      if (nextBreakout) {
        if (this.isTeacher) {
          this.refreshAssignments()
        }
      }
    },
    handleBreakoutClick() {
      this.togglePanel('breakout')
    },
    async assignStudentToRoom(studentName, targetRoomName) {
      this.isRoomsLoading = true
      this.roomsLoadingLabel = `Assigning ${studentName}…`
      try {
        await App.service('breakout-rooms').create({
          sid: this.classSid,
          assignments: [{userName: studentName, roomName: targetRoomName}],
        })

        // 🚀 1. Optimistic local update
        this.syncDashboardState(studentName, targetRoomName)

        // 🔄 2. Force UI refresh (Flicker)
        this.isRoomsDashboardOpen = false
        this.$nextTick(() => {
          this.isRoomsDashboardOpen = true
        })

        // 📡 3. Server re-fetch for absolute consistency
        this.refreshAssignments()

        this.showToast({type: 'positive', message: `Moved ${studentName} to ${targetRoomName}`})
      } catch (err) {
      } finally {
        this.isRoomsLoading = false
        this.roomsLoadingLabel = ''
      }
    },
    handleParticipantAssign(studentName, targetRoomName, sid) {
      this.$set(this.participantAssignMenuState, sid, false)
      this.assignStudentToRoom(studentName, targetRoomName)
    },
    async leaveBreakoutRoom() {
      await this.joinBreakoutRoom('MainRoom')
    },
    handleLeaveBreakout() {
      this.leaveBreakoutRoom()
      this.isLeaveMenuOpen = false
    },
    handleLeaveMeeting() {
      this.handleLeaveClick()
      this.isLeaveMenuOpen = false
    },
    async handleLeaveClick() {
      if (this.isTeacher) {
        if (confirm('Are you sure you want to end the meeting for everyone?')) {
          await this.endMeetingForAll()
        }
      } else {
        await this.handleLeave()
      }
    },
    async endMeetingForAll() {
      try {
        await App.service('breakout-rooms').remove(this.roomId)
        await this.handleLeave()
      } catch (err) {
        await this.handleLeave()
      }
    },
    broadcastSelfAssignment(targetRoomName) {
      this.syncDashboardState(this.userName, targetRoomName)
      App.service('breakout-rooms')
        .create({
          sid: this.classSid,
          assignments: [{userName: this.userName, roomName: targetRoomName, uid: window.currentUserId || ''}],
        })
        .catch((err) => {})
    },
    toggleMoveMenu(event, userName) {
      const isOpening = !this.moveMenuState[userName]

      this.moveMenuState = {}
      this.$set(this.moveMenuState, userName, isOpening)
      if (isOpening) {
        const rect = event.target.getBoundingClientRect()
        const menuWidth = 160
        const menuHeight = 200

        let top = rect.bottom + 6
        let left = rect.left - (menuWidth - 40)

        if (top + menuHeight > window.innerHeight) {
          top = rect.top - menuHeight - 6
        }

        this.$set(this.moveMenuStyles, userName, {
          position: 'fixed',
          top: top + 'px',
          left: left + 'px',
          zIndex: 100000,
        })
      }
    },
    getRoomTitle(roomName) {
      if (!roomName) return ''
      const cleanName = roomName.replace(new RegExp(`^${this.classSid}__`), '')
      if (cleanName === 'MainRoom' || cleanName === 'mainroom' || cleanName === 'Main Session' || roomName === `MainRoom_${this.classSid}`)
        return 'Main Session'
      if (cleanName.startsWith('Team-')) {
        const id = cleanName.split('-')[1]
        const team = (this.gameOnRecord?.teams || []).find((t) => String(t.id) === String(id))
        return team?.name || `Team ${id}`
      }
      return cleanName
    },
    async createBreakoutRoom() {
      if (this.isRoomsLoading) return

      let record = this.gameOnRecord
      if (!record) {
        this.isRoomsLoading = true
        this.roomsLoadingLabel = 'Preparing…'
        try {
          record = await App.service('game-on').create({
            sid: this.classSid || window.App.get?.('user')?.id,
            status: 0,
            teams: [],
          })
          this.gameOnRecord = record
        } catch (e) {
          this.isRoomsLoading = false
          this.roomsLoadingLabel = ''
          return
        }
        this.isRoomsLoading = false
        this.roomsLoadingLabel = ''
      }

      const newName = prompt('Enter room name:', `Team ${(record.teams || []).length + 1}`)
      if (!newName) return

      this.isRoomsLoading = true
      this.roomsLoadingLabel = 'Creating room…'
      const teams = [...(this.gameOnRecord.teams || [])]
      const maxId = teams.reduce((max, t) => Math.max(max, parseInt(t.id) || 0), 0)
      const newId = maxId + 1

      teams.push({
        id: newId,
        name: newName,
        members: [],
      })

      this.gameOnRecord.teams = teams

      try {
        await App.service('game-on').patch(this.gameOnRecord._id, {teams, sid: this.classSid})
        this.showToast({message: `Room "${newName}" created`, type: 'success'})
      } catch (err) {
        this.showToast({message: 'Failed to create room', type: 'error'})
      } finally {
        this.isRoomsLoading = false
      }
    },
    async promptRenameRoom(roomKey) {
      if (!this.gameOnRecord || this.isRoomsLoading) return
      const teamId = roomKey.startsWith('Team-') ? roomKey.split('-')[1] : null
      if (!teamId) return

      const teams = [...(this.gameOnRecord.teams || [])]
      const teamIdx = teams.findIndex((t) => String(t.id) === String(teamId))
      if (teamIdx === -1) return

      const newName = prompt('Rename room:', teams[teamIdx].name || `Team ${teamId}`)
      if (!newName || newName === teams[teamIdx].name) return

      this.isRoomsLoading = true
      this.roomsLoadingLabel = 'Renaming room…'
      teams[teamIdx].name = newName

      this.gameOnRecord.teams = teams

      try {
        await App.service('game-on').patch(this.gameOnRecord._id, {teams, sid: this.classSid})
        this.showToast({message: 'Room renamed', type: 'success'})
      } catch (err) {
      } finally {
        this.isRoomsLoading = false
        this.roomsLoadingLabel = ''
      }
    },
    async confirmDeleteRoom(roomKey) {
      if (!this.gameOnRecord || this.isRoomsLoading) return
      const teamId = roomKey.startsWith('Team-') ? roomKey.split('-')[1] : null
      if (!teamId) return

      if (!confirm(`Are you sure you want to delete this room? Users in it will be moved to the Main Session.`)) return

      this.isRoomsLoading = true
      this.roomsLoadingLabel = 'Deleting room…'
      const teams = (this.gameOnRecord.teams || []).filter((t) => String(t.id) !== String(teamId))

      this.gameOnRecord.teams = teams

      try {
        const membersInDeletedRoom = this.allRoomAssignments[roomKey] || []
        if (membersInDeletedRoom.length > 0) {
          const reassignments = membersInDeletedRoom.map((m) => ({userName: m.userName, roomName: 'MainRoom'}))
          await App.service('breakout-rooms').create({
            sid: this.classSid,
            assignments: reassignments,
          })
        }

        await App.service('game-on').patch(this.gameOnRecord._id, {teams, sid: this.classSid})

        if (membersInDeletedRoom.length > 0) {
          membersInDeletedRoom.forEach((m) => {
            this.syncDashboardState(m.userName, 'MainRoom')
          })
        }

        this.showToast({message: 'Room deleted', type: 'info'})

        setTimeout(() => this.refreshAssignments(), 1000)
      } catch (err) {
      } finally {
        this.isRoomsLoading = false
        this.roomsLoadingLabel = ''
      }
    },
    handleAssign(userName, targetRoom) {
      this.assignStudentToRoom(userName, targetRoom)
      this.moveMenuState[userName] = false
    },
    syncDashboardState(participantName, targetRoom) {
      const next = {...this.allRoomAssignments}

      Object.keys(next).forEach((r) => {
        if (Array.isArray(next[r])) {
          next[r] = next[r].filter((m) => m.userName !== participantName)
        }
      })

      if (targetRoom) {
        let normalizedRoom = targetRoom
        const clean = normalizedRoom.toLowerCase()
        if (
          clean === 'mainroom' ||
          clean === 'main session' ||
          (this.classSid && (normalizedRoom === `MainRoom_${this.classSid}` || normalizedRoom === `${this.classSid}__MainRoom`))
        ) {
          normalizedRoom = 'MainRoom'
        }
        if (!next[normalizedRoom]) this.$set(next, normalizedRoom, [])
        next[normalizedRoom] = [...next[normalizedRoom], {userName: participantName, roomName: normalizedRoom}]
      }
      this.allRoomAssignments = next
    },
    async refreshAssignments() {
      if (!this.classSid || !App || !App.service('breakout-rooms')) return
      try {
        const data = await App.service('breakout-rooms').find({
          query: {sid: this.classSid},
        })
        this.allRoomAssignments = data
      } catch (err) {}
    },
    setupRoomListeners(room) {
      room
        .on(RoomEvent.ParticipantConnected, (p) => {
          this.updateParticipantState(p)
          if (this.isTeacher && this.isWhiteboardActive) {
            const opts = p.identity ? {destinationIdentities: [p.identity]} : {}
            const self = this
            const sendWBState = () => {
              if (!self.isWhiteboardActive) return // whiteboard was closed
              self.sendWhiteboardData({action: 'start'}, opts)
              if (self.strokeHistory.length > 0) {
                self.sendWhiteboardData(
                  {
                    action: 'sync_history',
                    history: self.strokeHistory,
                    studentsCanDraw: self.studentsCanDraw,
                  },
                  opts
                )
              } else {
                self.sendWhiteboardData({action: 'allow_draw', value: self.studentsCanDraw}, opts)
              }
            }
            // Send immediately + retries to handle recorder data channel readiness
            sendWBState()
            setTimeout(sendWBState, 500)
            setTimeout(sendWBState, 2000)
            setTimeout(sendWBState, 4000)
          }
        })
        .on(RoomEvent.ParticipantDisconnected, (p) => this.removeParticipant(p))
        .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          if (track.kind === Track.Kind.Audio) {
            console.log(`[LiveKit] Audio track subscribed — source: ${publication.source}, participant: ${participant.identity}, trackSid: ${track.sid}`)
            const element = track.attach()
            element.id = `lk-audio-${track.sid}`
            element.muted = false
            element.volume = 1.0
            element.autoplay = true
            document.body.appendChild(element)
            const playPromise = element.play()
            if (playPromise) {
              playPromise.catch((err) => {
                console.warn(`[LiveKit] Audio autoplay blocked (source: ${publication.source}):`, err)
                // Retry playback on next user interaction (browser autoplay policy)
                const resumeAudio = () => {
                  element.play().catch(() => {})
                  document.removeEventListener('click', resumeAudio)
                  document.removeEventListener('touchstart', resumeAudio)
                }
                document.addEventListener('click', resumeAudio, {once: true})
                document.addEventListener('touchstart', resumeAudio, {once: true})
              })
            }
          }
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
          track.detach().forEach((el) => el.remove())
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.TrackUnpublished, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.LocalTrackPublished, (publication, participant) => this.updateParticipantState(participant))
        .on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
          if (publication.source === Track.Source.ScreenShare) {
            if (this.isAnnotating) this.isAnnotating = false
            this.isScreenSharing = false
            this.strokeHistory = []
            this.redoStack = []
            this.$nextTick(() => this.redrawCanvas())
            this.updateParticipantState(participant)
          }
        })
        .on(RoomEvent.TrackMuted, async (publication, participant) => {
          if (publication.source === Track.Source.ScreenShare) {
            if (participant.isLocal) {
              if (this.isAnnotating) this.isAnnotating = false
              this.isScreenSharing = false
              this.strokeHistory = []
              this.redoStack = []
              this.$nextTick(() => this.redrawCanvas())
              this.updateParticipantState(participant)
              await participant.setScreenShareEnabled(false)
            } else {
              this.updateParticipantState(participant)
            }
          } else {
            this.updateParticipantState(participant)
          }
        })
        .on(RoomEvent.TrackUnmuted, (publication, participant) => {
          this.updateParticipantState(participant)
        })
        .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          if (speakers.length > 0) this.activeSpeakerSid = speakers[0].sid
        })
        .on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const text = new TextDecoder().decode(payload)
            const msg = JSON.parse(text)
            if (msg.type === 'chat') {
              this.chatMessages.push({
                sender: msg.sender,
                text: msg.text,
                time: new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}),
                isMe: false,
              })
              if (!this.isChatOpen) {
                this.hasUnreadMessages = true
              }
              this.$nextTick(() => this.scrollChatToBottom())
            } else if (msg.type === 'whiteboard') {
              if (msg.action === 'start') {
                this.isWhiteboardActive = true
                this.strokeHistory = []
                this.redoStack = []
                if (this.activeScreenShare && this.room && this.activeScreenShare.sid === this.room.localParticipant.sid) {
                  this.room.localParticipant.setScreenShareEnabled(false)
                }
                this.$nextTick(() => {
                  this.resizeCanvas()
                  this.redrawCanvas()
                })
              } else if (msg.action === 'stop') {
                this.isWhiteboardActive = false
                this.strokeHistory = []
                this.redoStack = []
                this.$nextTick(() => this.redrawCanvas())
              } else if (msg.action === 'allow_draw') {
                this.studentsCanDraw = msg.value
              } else if (msg.action === 'sync_history') {
                this.strokeHistory = msg.history || []
                if (msg.studentsCanDraw !== undefined) {
                  this.studentsCanDraw = msg.studentsCanDraw
                }
                this.$nextTick(() => this.redrawCanvas())
              } else if (msg.action === 'request_state') {
                // Recorder bot (or late joiner) is requesting current whiteboard state
                if (this.isTeacher && this.isWhiteboardActive) {
                  const reqOpts = participant && participant.identity ? {destinationIdentities: [participant.identity]} : {}
                  this.sendWhiteboardData({action: 'start'}, reqOpts)
                  if (this.strokeHistory.length > 0) {
                    this.sendWhiteboardData({action: 'sync_history', history: this.strokeHistory, studentsCanDraw: this.studentsCanDraw}, reqOpts)
                  } else {
                    this.sendWhiteboardData({action: 'allow_draw', value: this.studentsCanDraw}, reqOpts)
                  }
                }
              }
            } else if (msg.type === 'reaction') {
              this.showReaction(participant.sid, msg.emoji)
            } else if (msg.type === 'annotation') {
              if (msg.action === 'start_stroke') {
                this.strokeHistory.push(msg.stroke)
                this.redoStack = []
                this.$nextTick(() => this.redrawCanvas())
              } else if (msg.action === 'draw_segment') {
                const stroke = this.strokeHistory.find((s) => s.id === msg.id)
                if (stroke) {
                  const prev = stroke.points[stroke.points.length - 1]
                  stroke.points.push(msg.point)
                  if (prev) {
                    this.drawTransientSegment(prev, msg.point, stroke.color, stroke.tool)
                  }
                }
              } else if (msg.action === 'undo') {
                if (this.strokeHistory.length > 0) {
                  this.redoStack.push(this.strokeHistory.pop())
                  this.$nextTick(() => this.redrawCanvas())
                }
              } else if (msg.action === 'redo') {
                if (this.redoStack.length > 0) {
                  this.strokeHistory.push(this.redoStack.pop())
                  this.$nextTick(() => this.redrawCanvas())
                }
              } else if (msg.action === 'clear') {
                this.strokeHistory = []
                this.redoStack = []
                this.$nextTick(() => this.redrawCanvas())
              } else if (msg.action === 'request_state') {
                // Recorder bot requesting annotation state
                if (this.isTeacher && this.strokeHistory.length > 0) {
                  const reqOpts = participant && participant.identity ? {destinationIdentities: [participant.identity]} : {}
                  this.sendAnnotationData({action: 'sync_history', history: this.strokeHistory}, reqOpts)
                }
              }
            }
          } catch (e) {}
        })
    },
    updateParticipantState(p) {
      const cameraPub = p.getTrackPublication(Track.Source.Camera)
      const micPub = p.getTrackPublication(Track.Source.Microphone)
      const screenPub = p.getTrackPublication(Track.Source.ScreenShare)

      const state = {
        sid: p.sid,
        identity: p.identity,
        name: p.identity || p.name || 'Unknown',
        isLocal: p.isLocal,
        videoTrack: cameraPub && cameraPub.track,
        audioTrack: micPub && micPub.track,
        screenTrack: screenPub && screenPub.track,
        isMicOn: p.isMicrophoneEnabled,
        isVideoOn: p.isCameraEnabled,
        isScreenSharing: p.isScreenShareEnabled && screenPub && !screenPub.isMuted,
      }

      if (p.isLocal) {
        this.isScreenSharing = state.isScreenSharing
        this.isMicOn = p.isMicrophoneEnabled
        this.isVideoOn = p.isCameraEnabled
      }

      const idx = this.participants.findIndex((item) => item.sid === p.sid)
      if (idx !== -1) {
        this.$set(this.participants, idx, state)
      } else {
        this.participants.push(state)
      }
    },
    removeParticipant(p) {
      const idx = this.participants.findIndex((item) => item.sid === p.sid)
      if (idx === -1) return

      this.participants.splice(idx, 1)

      // Removed: Automatic server-side move to 'MainRoom' on disconnection.
      // This causes race conditions during room switches.
    },
    bindTrack(el, track) {
      if (!el) return el
      if (track) {
        track.attach(el)
      } else {
        el.srcObject = null
      }
      return el
    },
    formatMessage(text) {
      if (!text) return ''
      // 1. Escape HTML to prevent XSS
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')

      // 2. Linkify URLs
      const urlRegex = /(https?:\/\/[^\s]+)/g
      return escaped.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" style="color: #4dabf7; text-decoration: underline; word-break: break-all;">${url}</a>`
      })
    },
    startAudioLevelPolling() {
      if (this.audioLevelInterval) return
      this.audioLevelInterval = setInterval(() => {
        if (this.room && this.room.localParticipant && this.isMicOn) {
          this.localAudioLevel = this.room.localParticipant.audioLevel
        } else {
          this.localAudioLevel = 0
        }
      }, 50)
    },
    stopAudioLevelPolling() {
      if (this.audioLevelInterval) {
        clearInterval(this.audioLevelInterval)
        this.audioLevelInterval = null
      }
      this.localAudioLevel = 0
    },
    async toggleMic() {
      if (!this.hasMicPermission) {
        this.mediaPermissionType = 'mic'
        this.showMediaPermissionModal = true
        return
      }
      try {
        this.isMicOn = !this.isMicOn
        if (this.room && this.room.localParticipant) {
          await this.room.localParticipant.setMicrophoneEnabled(this.isMicOn)
          this.updateParticipantState(this.room.localParticipant)
        }
        this.hasMicPermission = true
      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          this.hasMicPermission = false
          this.isMicOn = false
          this.mediaPermissionType = 'mic'
          this.showMediaPermissionModal = true
        }
        this.showToast({type: 'warning', message: 'Microphone access denied.'})
      }
    },

    async retryMediaPermission() {
      this.showMediaPermissionModal = false

      try {
        if (this.mediaPermissionType === 'mic') {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          })
          stream.getTracks().forEach((track) => track.stop())

          this.hasMicPermission = true
          this.isMicOn = true
          if (this.room && this.room.localParticipant) {
            await this.room.localParticipant.setMicrophoneEnabled(true)
          }
        } else {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          })
          stream.getTracks().forEach((track) => track.stop())

          this.hasCameraPermission = true
          this.isVideoOn = true
          if (this.room && this.room.localParticipant) {
            await this.room.localParticipant.setCameraEnabled(true)
          }
        }
        this.mediaPermissionPermanentlyDenied = false
      } catch (err) {
        console.log(err)
        await this.handleMediaPermissionDenied()
      }
    },
    async handleMediaPermissionDenied() {
      try {
        const permissionName = this.mediaPermissionType === 'mic' ? 'microphone' : 'camera'
        const permission = await navigator.permissions.query({
          name: permissionName,
        })

        if (permission.state === 'denied') {
          this.mediaPermissionPermanentlyDenied = true
        } else {
          this.mediaPermissionPermanentlyDenied = false
        }

        this.showMediaPermissionModal = true
      } catch (e) {
        // Safari fallback
        this.mediaPermissionPermanentlyDenied = false
        this.showMediaPermissionModal = true
      }
    },
    async toggleVideo() {
      if (!this.hasCameraPermission) {
        this.mediaPermissionType = 'camera'
        this.showMediaPermissionModal = true
        return
      }
      try {
        this.isVideoOn = !this.isVideoOn
        if (this.room && this.room.localParticipant) {
          await this.room.localParticipant.setCameraEnabled(this.isVideoOn)
          this.updateParticipantState(this.room.localParticipant)
        }
        this.hasCameraPermission = true
      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          this.hasCameraPermission = false
          this.isVideoOn = false
          this.mediaPermissionType = 'camera'
          this.showMediaPermissionModal = true
        }
        this.showToast({type: 'warning', message: 'Camera access denied.'})
      }
    },
    async toggleBackgroundBlur() {
      if (this.backgroundEffect === 'blur') {
        this.backgroundEffect = 'none'
      } else {
        this.backgroundEffect = 'blur'
      }
      this.blurProcessor = null
      await this.applyBlurStateToLocalVideoTrack()
      this.isVideoMenuOpen = false
    },
    triggerBgImageUpload() {
      if (this.$refs.bgImageInput) {
        this.$refs.bgImageInput.click()
      }
    },
    async onBgImageUpload(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      if (this.backgroundImageUrl && this.backgroundImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.backgroundImageUrl)
      }

      this.backgroundImageUrl = URL.createObjectURL(file)
      this.backgroundEffect = 'image'
      this.blurProcessor = null

      await this.applyBlurStateToLocalVideoTrack()
      this.isVideoMenuOpen = false
    },
    async toggleBackgroundImage() {
      if (this.backgroundEffect === 'image') {
        this.backgroundEffect = 'none'
      } else {
        this.backgroundEffect = 'image'
      }
      this.blurProcessor = null
      await this.applyBlurStateToLocalVideoTrack()
      this.isVideoMenuOpen = false
    },
    async applyBlurStateToLocalVideoTrack() {
      if (!this.room || !this.room.localParticipant) return

      const cameraPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera)
      const track = cameraPub && (cameraPub.videoTrack || cameraPub.track)

      if (!track) return

      const activeProcessor = typeof track.getProcessor === 'function' ? track.getProcessor() : undefined

      if (this.backgroundEffect !== 'none') {
        try {
          if (!this.blurProcessor) {
            const {BackgroundProcessor} = await import('@livekit/track-processors')

            const options = {
              mode: this.backgroundEffect === 'blur' ? 'background-blur' : 'virtual-background',
            }
            if (this.backgroundEffect === 'blur') {
              options.blurRadius = 10
            } else if (this.backgroundEffect === 'image') {
              options.imagePath = this.backgroundImageUrl
            }

            this.blurProcessor = BackgroundProcessor(options)
          }
          await track.setProcessor(this.blurProcessor)
        } catch (err) {
          console.error('Failed to set background processor:', err)
          this.showToast({type: 'warning', message: 'Failed to apply background effect.'})
          this.backgroundEffect = 'none'
        }
      } else {
        if (!activeProcessor) return
        try {
          if (typeof track.stopProcessor === 'function') {
            await track.stopProcessor()
          }
        } catch (err) {
          console.error('Failed to stop background processor:', err)
        }
      }
    },
    async toggleScreenShare() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        const isIPad = /iPad|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document
        const message = isIPad
          ? 'Screen sharing is not supported on iPad/mobile browsers. Please use a pc for screen sharing.'
          : 'Screen sharing is not supported on this browser or device.'
        this.showToast({type: 'negative', message, timeout: 5000})
        return
      }

      if (!this.room) {
        this.showToast({type: 'warning', message: 'Meeting connection not ready.'})
        return
      }

      if (this.room.state !== 'connected') {
        let waited = 0
        while (this.room.state !== 'connected' && waited < 3000) {
          await new Promise((r) => setTimeout(r, 200))
          waited += 200
        }
        this.showToast({type: 'warning', message: 'Meeting connection not ready.'})
        return
      }

      if (!this.isScreenSharing && this.isWhiteboardActive) {
        this.isWhiteboardActive = false
        this.strokeHistory = []
        this.redoStack = []
        this.$nextTick(() => this.redrawCanvas())
        this.sendWhiteboardData({action: 'stop'})
      }

      try {
        const enabled = !this.isScreenSharing

        // Hint: sharing a Chrome Tab is the most reliable way to share audio on all platforms.
        // On macOS entire-screen capture cannot access system audio; on Windows users must
        // check "Share system audio". Chrome Tab sharing always includes tab audio.
        if (enabled) {
          this.showToast({
            type: 'info',
            message: '💡 Tip: To share audio, choose "Chrome Tab" in the share dialog and ensure the audio toggle is on.',
            timeout: 15000,
          })
        }

        await this.room.localParticipant.setScreenShareEnabled(enabled, {audio: true})
      } catch (err) {
        this.showToast({type: 'negative', message: `Screen share failed: ${err.message}`})
      }
    },
    handleMoreScreenShare() {
      this.toggleScreenShare()
      this.isMoreMenuOpen = false
    },
    handleMoreWhiteboard() {
      this.toggleWhiteboard()
      this.isMoreMenuOpen = false
    },
    handleMoreRecord() {
      this.toggleRecord()
      this.isMoreMenuOpen = false
    },
    handleMoreBreakout() {
      this.togglePanel('breakout')
      this.isMoreMenuOpen = false
    },
    handleMoreReaction(emoji) {
      this.handleReaction(emoji)
      this.isMoreMenuOpen = false
    },
    async toggleWhiteboard() {
      if (!this.room || this.room.state !== 'connected') return
      if (!this.isTeacher) return

      const newState = !this.isWhiteboardActive
      this.isWhiteboardActive = newState

      if (newState) {
        this.strokeHistory = []
        this.redoStack = []
        if (this.isScreenSharing) {
          await this.room.localParticipant.setScreenShareEnabled(false)
        }
        this.$nextTick(() => {
          this.resizeCanvas()
          this.redrawCanvas()
        })
      } else {
        this.strokeHistory = []
        this.redoStack = []
        this.$nextTick(() => this.redrawCanvas())
      }
      this.sendWhiteboardData({action: newState ? 'start' : 'stop'})
    },
    exportWhiteboard() {
      if (!this.$refs.annotationCanvasRef) return
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = this.$refs.annotationCanvasRef.width
      tempCanvas.height = this.$refs.annotationCanvasRef.height
      const tCtx = tempCanvas.getContext('2d')
      tCtx.fillStyle = '#FFFFFF'
      tCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)
      tCtx.drawImage(this.$refs.annotationCanvasRef, 0, 0)

      const a = document.createElement('a')
      a.href = tempCanvas.toDataURL('image/png')
      a.download = `Whiteboard_${this.roomId}.png`
      a.click()
    },
    toggleStudentDrawing() {
      if (!this.isTeacher) return
      this.studentsCanDraw = !this.studentsCanDraw
      this.sendWhiteboardData({action: 'allow_draw', value: this.studentsCanDraw})
    },
    async sendWhiteboardData(data, options = {}) {
      if (!this.room || this.room.state !== 'connected') return
      const msg = JSON.stringify({type: 'whiteboard', ...data})
      const encoded = new TextEncoder().encode(msg)
      try {
        await this.room.localParticipant.publishData(encoded, {reliable: true, ...options})
      } catch (err) {}
    },

    async toggleRecord() {
      if (!this.isTeacher) {
        this.showToast({
          type: 'warning',
          message: 'Only the teacher can start or stop recording.',
        })
        return
      }

      // Pass a 30s timeout so the FeathersJS socket client waits long enough
      // for the gRPC Egress call to succeed or fail (default is only 5s)
      const callOpts = {timeout: 30000}

      const scopedRoom = this.roomId.includes(this.classSid) ? this.roomId : `${this.classSid}__${this.roomId}`

      if (this.isRecording) {
        // --- Stop Recording ---
        try {
          await App.service('livekit-recording').remove(scopedRoom, {...callOpts, query: {sid: this.classSid}})
          // The 'recording-stopped' socket event handles download + success notification
        } catch (err) {
          console.error('[RECORDING] Failed to stop:', err)
          this.showToast({type: 'negative', message: `Failed to stop recording: ${err.message}`})
        }
      } else {
        // --- Start Recording ---
        this.isStartingRecording = true
        try {
          await App.service('livekit-recording').create({roomName: scopedRoom, sid: this.classSid}, callOpts)
          this.ownedRecording = true
          // The 'recording-started' socket event sets isRecording + shows notification
        } catch (err) {
          this.isStartingRecording = false
          console.error('[RECORDING] Failed to start:', err)
          this.showToast({type: 'negative', message: `Failed to start recording: ${err.message}`})
        }
      }
    },
    async sendMessage() {
      const text = this.chatInput.trim()
      if (!text || !this.room || this.room.state !== 'connected') return

      const msg = JSON.stringify({type: 'chat', sender: this.userName, text})
      const encoded = new TextEncoder().encode(msg)

      try {
        await this.room.localParticipant.publishData(encoded, {reliable: true})
        this.chatMessages.push({
          sender: this.userName,
          text,
          time: new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}),
          isMe: true,
        })
        this.chatInput = ''
        this.$nextTick(() => this.scrollChatToBottom())
      } catch (err) {}
    },
    scrollChatToBottom() {
      if (this.$refs.chatScrollRef) {
        this.$refs.chatScrollRef.scrollTop = this.$refs.chatScrollRef.scrollHeight
      }
    },
    async handleLeave() {
      this.stopAudioLevelPolling()
      await this.stopHeartbeat()
      if (this.room) {
        await this.room.disconnect()
      }
      if (window.parent !== window) {
        window.parent.postMessage('livekit-leave', '*')
      }
      window.close()
    },
    sendReaction(emoji) {
      if (!this.room || this.room.state !== 'connected') return
      if (this.room.localParticipant) {
        this.showReaction(this.room.localParticipant.sid, emoji)
      }
      const msg = JSON.stringify({type: 'reaction', emoji})
      const encoded = new TextEncoder().encode(msg)
      this.room.localParticipant.publishData(encoded, {reliable: true}).catch((err) => {})
    },
    handleReaction(emoji) {
      this.sendReaction(emoji)
      this.isReactionsMenuOpen = false
    },
    showReaction(sid, emoji) {
      if (!this.activeReactions[sid]) {
        this.$set(this.activeReactions, sid, [])
      }
      const id = Date.now() + Math.random().toString()
      this.activeReactions[sid].push({id, emoji})

      setTimeout(() => {
        if (this.activeReactions[sid]) {
          const idx = this.activeReactions[sid].findIndex((r) => r.id === id)
          if (idx !== -1) {
            this.activeReactions[sid].splice(idx, 1)
          }
        }
      }, 3000)
    },
    startHeartbeat() {
      if (this.classSid) {
        localStorage.setItem('meeting_active_' + this.classSid, 'true')
        localStorage.removeItem('meeting_stopped_at_' + this.classSid)
      }
      this.heartbeatInterval = setInterval(async () => {
        if (this.sessionId && App && App.service('session')) {
          try {
            await App.service('session').patch(this.sessionId, {lastMeetingHeartbeat: Date.now()})
          } catch (e) {
            console.error('Heartbeat failed', e)
          }
        }
      }, 10000)
    },
    async stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
        this.heartbeatInterval = null
      }
      if (this.classSid) {
        localStorage.removeItem('meeting_active_' + this.classSid)
        localStorage.setItem('meeting_stopped_at_' + this.classSid, Date.now())
      }
      if (this.sessionId && App && App.service('session')) {
        try {
          await App.service('session').patch(this.sessionId, {lastMeetingHeartbeat: 0})
        } catch (e) {}
      }
    },
    resizeCanvas() {
      if (this.$refs.annotationCanvasRef && (this.activeScreenShare || this.isWhiteboardActive)) {
        this.$refs.annotationCanvasRef.width = this.$refs.annotationCanvasRef.clientWidth
        this.$refs.annotationCanvasRef.height = this.$refs.annotationCanvasRef.clientHeight
        this.redrawCanvas()
      }
    },
    toggleAnnotation() {
      this.isAnnotating = !this.isAnnotating
      if (this.isAnnotating && this.$refs.annotationCanvasRef) {
        this.drawingCtx = this.$refs.annotationCanvasRef.getContext('2d')
        if (this.drawingCtx) {
          this.drawingCtx.lineCap = 'round'
          this.drawingCtx.lineJoin = 'round'
        }
        this.redrawCanvas()
      }
    },
    generateId() {
      return Math.random().toString(36).substr(2, 9)
    },
    ensureContext() {
      if (!this.drawingCtx && this.$refs.annotationCanvasRef) {
        this.drawingCtx = this.$refs.annotationCanvasRef.getContext('2d')
      }
      if (this.drawingCtx) {
        this.drawingCtx.lineCap = 'round'
        this.drawingCtx.lineJoin = 'round'
      }
    },
    redrawCanvas() {
      const canvas = this.$refs.annotationCanvasRef
      if (canvas && (Math.floor(canvas.width) !== Math.floor(canvas.clientWidth) || Math.floor(canvas.height) !== Math.floor(canvas.clientHeight))) {
        canvas.width = canvas.clientWidth
        canvas.height = canvas.clientHeight
      }
      this.ensureContext()
      if (!canvas || !this.drawingCtx) return

      const w = canvas.width
      const h = canvas.height

      this.drawingCtx.clearRect(0, 0, w, h)

      for (const stroke of this.strokeHistory) {
        if (stroke.points.length < 2) continue

        this.drawingCtx.beginPath()
        this.drawingCtx.strokeStyle = stroke.color
        this.drawingCtx.lineWidth = stroke.tool === 'eraser' ? 20 : 3
        this.drawingCtx.globalCompositeOperation = stroke.tool === 'eraser' ? 'destination-out' : 'source-over'

        this.drawingCtx.moveTo(stroke.points[0].x * w, stroke.points[0].y * h)
        for (let i = 1; i < stroke.points.length; i++) {
          this.drawingCtx.lineTo(stroke.points[i].x * w, stroke.points[i].y * h)
        }
        this.drawingCtx.stroke()
      }

      this.drawingCtx.globalCompositeOperation = 'source-over'
    },
    drawTransientSegment(p1, p2, color, tool) {
      const canvas = this.$refs.annotationCanvasRef
      if (canvas && (Math.floor(canvas.width) !== Math.floor(canvas.clientWidth) || Math.floor(canvas.height) !== Math.floor(canvas.clientHeight))) {
        canvas.width = canvas.clientWidth
        canvas.height = canvas.clientHeight
        this.redrawCanvas()
      }
      this.ensureContext()
      if (!this.drawingCtx || !canvas) return
      const w = canvas.width
      const h = canvas.height

      this.drawingCtx.beginPath()
      this.drawingCtx.strokeStyle = color
      this.drawingCtx.lineWidth = tool === 'eraser' ? 20 : 3
      this.drawingCtx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over'

      this.drawingCtx.moveTo(p1.x * w, p1.y * h)
      this.drawingCtx.lineTo(p2.x * w, p2.y * h)
      this.drawingCtx.stroke()

      this.drawingCtx.globalCompositeOperation = 'source-over'
    },
    startDrawing(e) {
      if (!this.isAnnotating && !this.isWhiteboardActive) return
      if (!this.$refs.annotationCanvasRef) return
      if (!this.isTeacher && !this.studentsCanDraw) {
        this.showToast({type: 'warning', message: 'Teacher disabled student drawing.'})
        return
      }
      this.isDrawing = true
      this.ensureContext()

      const rect = this.$refs.annotationCanvasRef.getBoundingClientRect()
      const clientX = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX
      const clientY = e.touches && e.touches.length > 0 ? e.touches[0].clientY : e.clientY
      const point = {
        x: (clientX - rect.left) / rect.width,
        y: (clientY - rect.top) / rect.height,
      }

      this.currentStrokeId = this.generateId()
      const stroke = {
        id: this.currentStrokeId,
        tool: this.activeTool,
        color: this.currentColor,
        points: [point],
      }

      this.strokeHistory.push(stroke)
      this.redoStack = []
      this.sendAnnotationData({action: 'start_stroke', stroke})
    },
    draw(e) {
      if (!this.isDrawing || (!this.isAnnotating && !this.isWhiteboardActive) || !this.$refs.annotationCanvasRef) return
      const stroke = this.strokeHistory.find((s) => s.id === this.currentStrokeId)
      if (!stroke) return

      const rect = this.$refs.annotationCanvasRef.getBoundingClientRect()
      const clientX = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX
      const clientY = e.touches && e.touches.length > 0 ? e.touches[0].clientY : e.clientY
      const point = {
        x: (clientX - rect.left) / rect.width,
        y: (clientY - rect.top) / rect.height,
      }

      const prevPoint = stroke.points[stroke.points.length - 1]
      stroke.points.push(point)

      this.drawTransientSegment(prevPoint, point, stroke.color, stroke.tool)
      this.sendAnnotationData({action: 'draw_segment', id: this.currentStrokeId, point})
    },
    stopDrawing() {
      this.isDrawing = false
      this.currentStrokeId = null
    },
    undoAnnotation() {
      if (!this.canDraw) return
      if (this.strokeHistory.length > 0) {
        this.redoStack.push(this.strokeHistory.pop())
        this.redrawCanvas()
        this.sendAnnotationData({action: 'undo'})
      }
    },
    redoAnnotation() {
      if (!this.canDraw) return
      if (this.redoStack.length > 0) {
        this.strokeHistory.push(this.redoStack.pop())
        this.redrawCanvas()
        this.sendAnnotationData({action: 'redo'})
      }
    },
    clearAnnotation() {
      if (!this.canDraw) return
      this.strokeHistory = []
      this.redoStack = []
      this.redrawCanvas()
      this.sendAnnotationData({action: 'clear'})
    },
    async sendAnnotationData(data, options = {}) {
      if (!this.room || this.room.state !== 'connected') return
      const msg = JSON.stringify({type: 'annotation', ...data})
      const encoded = new TextEncoder().encode(msg)
      try {
        await this.room.localParticipant.publishData(encoded, {reliable: false, ...options})
      } catch (err) {}
    },
    getAvatarColor(name) {
      if (!name) return '#2d8cff'
      const hash = [...name].reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0)
      const colors = ['#2d8cff', '#00c853', '#ff6d00', '#d500f9', '#ff1744', '#00b0ff', '#3d5afe', '#009688', '#795548']
      return colors[Math.abs(hash) % colors.length]
    },
  },
}
</script>

<style scoped>
.room-header-hover {
  transition: background-color 0.2s ease;
}

.room-header-hover:hover {
  background-color: #303134 !important;
}

.room-member-hover {
  transition: background-color 0.2s ease;
}

.room-member-hover:hover {
  background-color: #303134 !important;
}

.zoom-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #000000;
  position: relative;
}

.zoom-header {
  height: 48px;
  background-color: #1a1a1a;
  border-bottom: 1px solid #333;
  z-index: 10;
}

.main-stage {
  flex-grow: 1;
  position: relative;
  display: flex;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
  min-height: 0;
}

.stage-layout {
  display: flex;
  width: 100%;
  height: 100%;
  gap: 16px;
}

.primary-view {
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #000000;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  border: 1px solid #333;
}

.primary-view.bg-white {
  background-color: #ffffff !important;
}

.full-video {
  width: 100%;
  height: 100%;
}

.object-contain {
  object-fit: contain;
}

.annotation-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  touch-action: none;
}

.pointer-events-auto {
  pointer-events: auto;
}

.pointer-events-none {
  pointer-events: none;
}

.annotation-toolbar {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
}

.tools-tray {
  background: rgba(40, 40, 40, 0.95);
  backdrop-filter: blur(10px);
  padding: 6px;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.tool-btn {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  cursor: pointer;
}
.tool-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
}
.tool-btn.active {
  background: #1976d2;
  color: #fff;
}
.tool-btn .material-icons {
  font-size: 20px;
}

.participant-sidebar {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding-right: 4px;
}

.sidebar-tile {
  width: 100%;
  height: 135px;
  background: #1a1a1a;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.gallery-layout {
  display: grid;
  gap: 12px;
  padding: 24px;
  box-sizing: border-box;
  height: calc(100vh - 48px - 90px - 32px);
  align-content: start;
  width: 100%;
  max-width: 1600px;
  overflow-y: auto;
}
.modal-body.scroll {
  overflow-y: auto;
}
.video-card {
  position: relative;
  background: #1a1a1a;
  border-radius: 14px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  border: 2px solid transparent;
  transition: transform 0.3s cubic-bezier(0.2, 0, 0, 1), border-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.video-card:hover {
  transform: translateY(-2px);
}

.gallery-layout.grid-1 {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  max-width: 1000px;
  margin: 0 auto;
}

.gallery-layout.grid-2 {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
}

.gallery-layout.grid-4 {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
}

.gallery-layout.grid-6 {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
}

.gallery-layout.grid-many {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-auto-rows: 240px;
  align-content: start;
}

.video-mirror {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.full-video {
  width: 100%;
  height: 100%;
  background: #000;
}

.tile-label {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  z-index: 10;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.small-text {
  font-size: 0.65rem;
  padding: 1px 6px;
}

.speaker-active {
  border-color: #2d8cff;
}

.zoom-footer {
  height: 90px;
  background: #1a1a1a;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 100;
}

.icon-stack {
  position: relative;
  display: flex;
  align-items: center;
}

.dropdown-arrow {
  position: absolute;
  right: -4px;
  bottom: 8px;
  color: #888;
}

.permission-warning {
  position: absolute;
  bottom: 0px;
  right: -4px;
  color: #ff0050;
  font-size: 16px !important;
  background: #1a1a1a;
  border-radius: 4px;
  z-index: 2;
}

.control-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  min-width: 80px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  position: relative;
}

.control-unit:hover {
  background: rgba(255, 255, 255, 0.08);
}

.control-text {
  font-size: 12px;
  font-weight: 500;
  color: #efefef;
  margin-top: 4px;
  white-space: nowrap;
}

.leave-button {
  border-radius: 10px !important;
  font-weight: 700 !important;
  height: 40px;
}

.chat-panel,
.participants-panel {
  position: absolute;
  right: 12px;
  top: 60px;

  bottom: 102px;

  width: 360px;
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  z-index: 200;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
  overflow: hidden;
}

.participants-panel {
  z-index: 210;
}

.participants-list {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #444 transparent;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #2a2a2a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.95rem;
  font-weight: 700;
  color: #eef;
}

.chat-title {
  display: flex;
  align-items: center;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: #444 transparent;
}

.chat-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 40px;
}

.chat-bubble {
  display: flex;
  flex-direction: column;
  max-width: 88%;
}

.bubble-me {
  align-self: flex-end;
  align-items: flex-end;
}

.bubble-them {
  align-self: flex-start;
  align-items: flex-start;
}

.bubble-sender {
  font-size: 0.65rem;
  color: #888;
  margin-bottom: 2px;
  padding: 0 4px;
}

.bubble-text {
  padding: 10px 16px;
  border-radius: 18px;
  font-size: 0.85rem;
  line-height: 1.5;
  word-break: break-word;
  color: #ffffff;
}

.bubble-me .bubble-text {
  background: #0b5cff;
  border-bottom-right-radius: 4px;
}

.bubble-them .bubble-text {
  background: #3c3c3e;
  border-bottom-left-radius: 4px;
}

.bubble-time {
  font-size: 0.7rem;
  color: #999;
  padding: 4px 8px;
}

.chat-input-area {
  padding: 16px 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: #1a1a1a;
}

.chat-input {
  width: 100%;
  background: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 10px 48px 10px 16px;
  color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.chat-input:focus {
  border-color: #0b5cff;
  box-shadow: 0 0 0 2px rgba(11, 92, 255, 0.2);
}

.chat-input::placeholder {
  color: #888;
}

.input-container .absolute-right {
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

.slide-chat-enter-active,
.slide-chat-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.slide-chat-enter-from,
.slide-chat-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.reaction-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 4px;
  pointer-events: none;
  z-index: 20;
}

.reaction-emoji {
  font-size: 32px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.5s ease;
}

.fade-up-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.5);
}

.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(1.5);
}

@keyframes recording-pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(1.25);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.recording-pulse-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f44336;
  animation: recording-pulse 1.5s ease-in-out infinite;
  margin-right: 6px;
  vertical-align: middle;
  box-shadow: 0 0 4px rgba(244, 67, 54, 0.4);
}

.recording-status {
  padding: 3px 10px;
  background: rgba(244, 67, 54, 0.12);
  border-radius: 12px;
  border: 1px solid rgba(244, 67, 54, 0.3);
  height: 22px;
  display: flex;
  align-items: center;
}

.tracking-widest {
  letter-spacing: 0.12em;
}

@media (max-width: 1023px) {
  .zoom-footer {
    height: 72px;
    padding: 0 12px !important;
  }

  .control-unit {
    min-width: 60px;
    padding: 4px;
  }

  .control-text {
    font-size: 10px;
  }

  .leave-button {
    height: 32px;
    font-size: 12px;
    padding: 4px 12px !important;
  }

  .stage-layout {
    flex-direction: column;
    padding-bottom: 8px;
  }

  .primary-view {
    flex: 1;
    min-height: 40vh;
  }

  .participant-sidebar {
    width: 100%;
    max-width: unset;
    height: auto;
    min-height: 135px;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 4px;
  }

  .sidebar-tile {
    width: 180px;
    height: 135px;
  }

  .annotation-toolbar {
    max-width: 90vw;
    overflow-x: auto;
  }
  .gallery-layout {
    padding: 12px;
    gap: 8px;
  }
  .gallery-layout.grid-many {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    grid-auto-rows: 150px;
  }

  .gallery-layout.grid-2 {
    grid-template-columns: 1fr !important;
    grid-template-rows: repeat(2, 1fr) !important;
  }

  .chat-panel,
  .participants-panel {
    width: 90vw;
    right: 5vw;
    top: 55px;
    bottom: 80px;
  }
}

@media (max-width: 599px) {
  .gallery-layout {
    padding: 12px;
    gap: 8px;
  }

  .gallery-layout.grid-many {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 160px;
  }

  .gallery-layout.grid-2,
  .gallery-layout.grid-4,
  .gallery-layout.grid-6 {
    grid-template-columns: 1fr 1fr;
  }

  .chat-panel,
  .participants-panel {
    width: 100vw;
    right: 0;
    top: 48px;
    bottom: 72px;
    border-radius: 0;
    border: none;
  }

  .zoom-footer {
    height: 70px;
    padding: 0 4px !important;
    justify-content: space-around !important;
    gap: 0 !important;
    position: relative;
  }

  .footer-left,
  .footer-center,
  .footer-right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 2px !important;
  }

  .control-unit {
    min-width: 48px !important;
    padding: 4px 2px !important;
    background: transparent !important;
  }

  .btn-round {
    width: 32px !important;
    height: 32px !important;
  }

  .control-unit .material-icons {
    font-size: 18px !important;
  }

  .annotation-toolbar {
    max-width: 95vw !important;
    top: 8px !important;
  }

  .tools-tray {
    padding: 4px !important;
    gap: 0 !important;
  }

  .tool-btn {
    width: 32px !important;
    height: 32px !important;
  }

  .tool-btn .material-icons {
    font-size: 20px !important;
  }

  .badge {
    padding: 1px 4px !important;
    font-size: 8px !important;
    top: -2px !important;
    right: -2px !important;
  }

  .control-text {
    display: none !important;
  }

  .leave-button {
    padding: 6px 10px !important;
    font-size: 11px !important;
    min-width: unset !important;
    white-space: nowrap;
  }

  .header-left {
    max-width: 80% !important;
  }

  .recording-pulse-dot {
    width: 6px !important;
    height: 6px !important;
    margin-right: 4px !important;
    margin-top: 2px !important;
  }
}

.text-lg {
  font-size: 24px !important;
}

.text-md {
  font-size: 18px !important;
}

.btn-xs {
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
}

.p-md {
  padding: 16px !important;
}

.space-between {
  justify-content: space-between !important;
}

.p-0 {
  padding: 0 !important;
}

.w-full {
  width: 100% !important;
}

.flex-col {
  flex-direction: column !important;
}

.items-stretch {
  align-items: stretch !important;
}

.bg-black-light {
  background: rgba(0, 0, 0, 0.3);
}

.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.hover-white:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-card {
  background: #202124;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
}

.breakout-modal {
  width: 500px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.join-modal {
  width: 450px;
  max-width: 95vw;
}

.modal-header {
  padding: 16px 20px;
  background: #252525;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.mac-dots {
  position: absolute;
  left: 16px;
  display: flex;
  gap: 8px;
}

.mac-dots .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot.red {
  background: #ff5f56;
  cursor: pointer;
}

.dot.yellow {
  background: #ffbd2e;
}

.dot.green {
  background: #27c93f;
}

.modal-body {
  padding: 0;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
}

.modal-section {
  padding: 24px;
}

.modal-footer {
  padding: 16px;
  background: #202124;
  border-top: 1px solid #333;
}

.btn {
  border: none;
  outline: none;
  cursor: pointer;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.pill-btn {
  border-radius: 30px !important;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.btn-primary {
  background: #2d8cff;
  color: #fff;
}

.btn-primary:hover {
  background: #2578d8;
}

.btn-outline {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #555;
  color: #eee;
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #888;
  color: #fff;
}

.btn-outline:disabled {
  opacity: 0.2;
  cursor: not-allowed;
  border-color: #333;
}

.btn-red {
  background: #e02828;
  color: #fff;
}

.btn-red:hover {
  background: #c02020;
}

.btn-blue {
  background: #2d8cff;
  color: #fff;
}

.btn-flat {
  background: transparent;
}

.btn-flat:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-round {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
}

.btn-sm {
  min-width: 64px;
  height: 32px;
  font-size: 13px;
  border-radius: 16px;
  padding: 0 16px;
}

.bg-transparent {
  background: transparent;
}

.input-label {
  display: block;
  font-size: 12px;
  color: #aaa;
  margin-bottom: 4px;
  text-align: left;
}

.custom-input {
  width: 100%;
  border-radius: 8px;
  padding: 12px;
  background: #2d2d2d;
  color: white;
  border: 1px solid #444;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.custom-input:focus {
  border-color: #2d8cff;
  box-shadow: 0 0 0 2px rgba(45, 140, 255, 0.2);
}

.inner-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(30, 30, 30, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.accordion-item {
  width: 100%;
  border-bottom: 1px solid #2a2a2a;
}

.accordion-header {
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #2a2a2a;
}

.accordion-header:hover {
  background: #2a2b2e;
}

.room-title {
  font-weight: 600;
  color: #fff;
  text-transform: capitalize;
  letter-spacing: 0.3px;
  margin-right: 24px;
}

.expand-icon {
  transition: transform 0.2s;
  color: #888;
  margin-right: 12px;
  font-size: 20px;
}

.member-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: transparent;
}

.accordion-content {
  max-height: 250px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.15);
}

.accordion-content::-webkit-scrollbar {
  width: 4px;
}
.accordion-content::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 2px;
}

.member-item {
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background-color 0.2s ease;
}

.member-item:hover {
  background: rgba(255, 255, 255, 0.03);
}

.btn-assign {
  background: transparent;
  border: 1.5px solid #2d8cff;
  color: #2d8cff;
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-assign:hover {
  background: #2d8cff;
  color: #fff;
}

.icon-status {
  padding: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
}

.separator-vertical {
  width: 1px;
  height: 24px;
  background: #666;
}

.color-picker-wrapper {
  position: relative;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
}

.color-picker-wrapper:hover {
  background: rgba(255, 255, 255, 0.1);
}

.color-picker-input {
  position: absolute;
  opacity: 0;
  width: 200%;
  height: 200%;
  cursor: pointer;
}

.row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px;
  box-sizing: border-box;
}

.col-6 {
  width: 50%;
  padding: 0 8px;
  box-sizing: border-box;
}

.full-width {
  width: 100%;
}

.flex {
  display: flex;
}

.space-between {
  justify-content: space-between;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.flex-end {
  justify-content: flex-end;
}

.column {
  display: flex;
  flex-direction: column;
}

.column-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mt-sm {
  margin-top: 8px;
}

.mt-xs {
  margin-top: 4px;
}

.mb-lg {
  margin-bottom: 24px;
}

.p-none {
  padding: 0;
}

.p-md {
  padding: 16px;
}

.p-sm {
  padding: 8px;
}

.pr-sm {
  padding-right: 8px;
}

.pl-sm {
  padding-left: 8px;
}

.pb-lg {
  padding-bottom: 24px;
}

.ml-sm {
  margin-left: 8px;
}

.mr-xs {
  margin-right: 4px;
}

.px-xl {
  padding-left: 48px;
  padding-right: 48px;
}

.px-md {
  padding-left: 16px;
  padding-right: 16px;
}

.px-lg {
  padding-left: 24px;
  padding-right: 24px;
}

.py-md {
  padding-top: 16px;
  padding-bottom: 16px;
}

.gap-md {
  gap: 16px;
}

.gap-sm {
  gap: 8px;
}

.gap-xs {
  gap: 4px;
}

.material-icons {
  font-size: 24px;
  line-height: 1;
}
.material-icons.text-sm {
  font-size: 18px !important;
}

.text-center {
  text-align: center;
}

.font-bold {
  font-weight: bold;
}

.font-medium {
  font-weight: 500;
}

.text-h5 {
  font-size: 24px;
}

.text-sm {
  font-size: 13px;
}

.text-primary {
  color: #1976d2;
}

.text-white {
  color: #fff;
}

.text-grey-4 {
  color: #bdbdbd;
}

.text-grey-5 {
  color: #9e9e9e;
}

.text-grey-6 {
  color: #757575;
}

.text-grey-7 {
  color: #616161;
}

.text-red {
  color: #f44336;
}

.text-green {
  color: #4caf50;
}

.text-amber {
  color: #ffb300;
}

.text-blue {
  color: #2196f3;
}

.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.absolute-right {
  position: absolute;
  right: 8px;
}

.badge {
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 2px 6px;
  font-size: 10px;
  border-radius: 10px;
  color: #fff;
}

.popover {
  position: absolute;
  background: #2d2d2d;
  border-radius: 8px;
  border: 1px solid #444;
  z-index: 300;
  overflow: hidden;
  width: max-content;
}

.popover-top {
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
}

.popover-top-right {
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
}

.popover-bottom-left {
  top: 100%;
  right: 0;
  margin-top: 8px;
}

.dropdown-list {
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 160px;
}

.dropdown-list li {
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
}

.dropdown-list li:hover:not(.no-hover) {
  background: rgba(255, 255, 255, 0.1);
}

.dropdown-list li.separator {
  padding: 0;
  height: 1px;
  background: #444;
  margin: 4px 0;
}

.dropdown-menu {
  background: #2a2a2e;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 4px 0;
  min-width: 160px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
}

.no-transition {
  transition: none !important;
}

.modal-body {
  padding-bottom: 20px !important;
}

.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}
.dropdown-menu::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 3px;
}

.dropdown-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-menu li {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #eee;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  text-align: left;
}

.dropdown-menu li:hover {
  background: #2d8cff;
  color: white;
}

.btn-text-blue {
  color: #2d8cff;
  font-weight: 600;
}

.btn-text-blue:hover {
  text-decoration: underline;
}

.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}

.toast {
  background: #333;
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  animation: toast-in 0.3s ease;
}

.toast-negative {
  background: #d32f2f;
}

.toast-positive {
  background: #388e3c;
}

.toast-warning {
  background: #f57c00;
}

@keyframes toast-in {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-container .btn-sm {
  transition: transform 0.2s;
}

.input-container .btn-sm:hover:not(:disabled) {
  transform: scale(1.1);
}

.input-container .btn-sm:active:not(:disabled) {
  transform: scale(0.9);
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
}

.bg-primary {
  background: #1976d2;
}
.bg-blue {
  background: #2196f3 !important;
}
.cursor-pointer {
  cursor: pointer;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: #2d8cff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-xs {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #2d8cff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

.breakout-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.12);
  border-top-color: #4fc3f7;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.breakout-updating-overlay {
  background: rgba(18, 18, 22, 0.82);
  backdrop-filter: blur(6px);
  border-radius: 12px;
}
.bg-red {
  background-color: red;
}
</style>
